import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import html2canvas from 'html2canvas';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';
import { 
    Sparkles, Camera, Upload, Image as ImageIcon, 
    Shirt, Download, Check, Loader2, Wand2,
    Clock, Trash2, User, Zap, Plus, Grid3X3,
    ChevronLeft, ChevronRight, X, Heart, Share2,
    Maximize2, ArrowRight, Layers, Save,
    Book, Eye, EyeOff, Filter, Search, GripVertical,
    LayoutGrid, Move, Palette, Tag, CheckCircle2,
    ArrowUp, ArrowDown, ChevronsUp, ChevronsDown,
    Lock, Unlock, Crown, ImagePlus, UserCircle2, Scissors, Clipboard,
    Type, Bold, Italic
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useToast } from '../components/ui/use-toast';
import { getApiUrl } from '../utils';
import { cacheGet } from '../utils/offlineCache';

const API_URL = getApiUrl();

// Category filters for garments
const garmentCategories = [
    { id: 'all', label: 'All', icon: Grid3X3 },
    { id: 'Tops', label: 'Tops', icon: Shirt },
    { id: 'Bottoms', label: 'Bottoms', icon: Shirt },
    { id: 'Dresses', label: 'Dresses', icon: Shirt },
    { id: 'Outerwear', label: 'Outerwear', icon: Shirt },
    { id: 'Accessories', label: 'Accessories', icon: Shirt },
    { id: 'ai-studio', label: '✨ AI Studio', icon: Sparkles },
    { id: 'stickers', label: '🎨 Stickers', icon: Scissors }
];

// Processing steps for AI try-on
const processingSteps = [
    { text: 'Analyzing your photo...', icon: User },
    { text: 'Detecting body pose...', icon: Sparkles },
    { text: 'Preparing garment...', icon: ImageIcon },
    { text: 'AI fitting in progress...', icon: Wand2 },
    { text: 'Finalizing your look...', icon: Zap }
];



export default function StyleStudio() {
    const { toast } = useToast();
    const navigate = useNavigate();
    // Main tab state
    const [activeTab, setActiveTab] = useState('builder'); // 'builder', 'tryon', 'history'
    
    // Wardrobe sidebar state
    const [wardrobeItems, setWardrobeItems] = useState([]);
    const [loadingWardrobe, setLoadingWardrobe] = useState(true);
    const [wardrobeSearch, setWardrobeSearch] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [showWardrobeSidebar, setShowWardrobeSidebar] = useState(window.innerWidth >= 1024);
    const [wardrobePanelMode, setWardrobePanelMode] = useState('half'); // 'half' | 'full'
    const panelTouchStartY = useRef(0);
    
    // Outfit builder state
    const [canvasItems, setCanvasItems] = useState([]);
    const [outfitName, setOutfitName] = useState('');
    const [outfitCategory, setOutfitCategory] = useState('');
    const [boardBg, setBoardBg] = useState('#ffffff');
    const [showDots, setShowDots] = useState(true);
    
    // Saved outfits filter
    const [selectedSavedCategory, setSelectedSavedCategory] = useState('all');
    
    // Editing existing outfit — when set, save will update instead of creating new
    const [editingOutfitId, setEditingOutfitId] = useState(null);
    
    // Save modal state
    const [showSaveModal, setShowSaveModal] = useState(false);
    const [saveOptions, setSaveOptions] = useState({
        toWardrobe: false,
        toStylebook: false,
        toStudioSaves: true,
        toSavedLooks: false,
        stylebookId: null,
        newStylebookName: '',
        collectionName: ''
    });
    const [stylebooks, setStylebooks] = useState([]);
    const [isSaving, setIsSaving] = useState(false);
    const [saveSuccess, setSaveSuccess] = useState(false);
    
    // Photo state for try-on
    const [userPhoto, setUserPhoto] = useState(null);
    const [userPhotoPreview, setUserPhotoPreview] = useState(null);
    const [hasSavedModel, setHasSavedModel] = useState(false);
    const [isLoadingModel, setIsLoadingModel] = useState(true);
    
    // Try-on state
    const [selectedGarmentForTryon, setSelectedGarmentForTryon] = useState(null);
    const [selectedTryOnGarments, setSelectedTryOnGarments] = useState([]);
    const [isProcessing, setIsProcessing] = useState(false);
    const [processingStep, setProcessingStep] = useState(0);
    const [tryOnResult, setTryOnResult] = useState(null);
    const [tryOnHistory, setTryOnHistory] = useState([]);
    const [error, setError] = useState(null);
    const [tryOnCategoryFilter, setTryOnCategoryFilter] = useState('all');
    
    // Canvas edit mode — items always editable by default, can be locked with toggle
    const [canvasEditMode, setCanvasEditMode] = useState(true);
    
    // Saved outfits state
    const [savedOutfits, setSavedOutfits] = useState([]);
    const [loadingSavedOutfits, setLoadingSavedOutfits] = useState(false);
    
    // UI state
    const [dragActive, setDragActive] = useState(false);
    const [selectedHistoryItem, setSelectedHistoryItem] = useState(null);
    const [showFullscreen, setShowFullscreen] = useState(false);
    const [draggingItem, setDraggingItem] = useState(null);
    
    // Subscription state
    const [userTier, setUserTier] = useState('free');
    const isPremiumUser = userTier === 'basic' || userTier === 'premium' || userTier === 'admin';
    
    // Premium photo upload with bg removal
    const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);
    const [removingBg, setRemovingBg] = useState(false);
    const photoUploadRef = useRef(null);
    const rawPhotoUploadRef = useRef(null);
    const bgUploadRef = useRef(null);
    
    // Custom background image
    const [boardBgImage, setBoardBgImage] = useState(null);
    
    // Virtual try-on models for canvas
    const [tryOnModels, setTryOnModels] = useState([]);
    const [loadingTryOnModels, setLoadingTryOnModels] = useState(false);
    const [showPremiumTools, setShowPremiumTools] = useState(false);
    const [showFreeTools, setShowFreeTools] = useState(true);
    
    // AI Studio saved creations for canvas
    const [aiStudioItems, setAiStudioItems] = useState([]);
    const [loadingAiStudio, setLoadingAiStudio] = useState(false);
    
    // Sticker items from clipboard paste
    const [stickerItems, setStickerItems] = useState([]);
    const [loadingStickers, setLoadingStickers] = useState(false);
    const [isPasting, setIsPasting] = useState(false);
    
    // Shapes for canvas
    const [shapeColor, setShapeColor] = useState('#000000');
    const [shapeStrokeWidth, setShapeStrokeWidth] = useState(2);
    
    const [token, setToken] = useState(() => localStorage.getItem('token'));

    // Retry reading token after short delays (covers Capacitor WebView cold-start on iPad)
    useEffect(() => {
        if (!token) {
            const t1 = setTimeout(() => { const t = localStorage.getItem('token'); if (t) setToken(t); }, 300);
            const t2 = setTimeout(() => { const t = localStorage.getItem('token'); if (t) setToken(t); }, 800);
            const t3 = setTimeout(() => { const t = localStorage.getItem('token'); if (t) setToken(t); }, 1500);
            return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
        }
    }, [token]);

    const canvasRef = useRef(null);
    const [activeCanvasDrag, setActiveCanvasDrag] = useState(null); // { canvasId, offsetX, offsetY }
    const [activeResize, setActiveResize] = useState(null); // { canvasId, startX, startW }
    const [activeRotation, setActiveRotation] = useState(null); // { canvasId, centerX, centerY, startAngle }
    const [selectedCanvasItem, setSelectedCanvasItem] = useState(null);
    const [editingTextItem, setEditingTextItem] = useState(null); // canvasId of text item being inline-edited
    
    // Use refs to always have current canvasItems without breaking event listeners
    const canvasItemsRef = useRef(canvasItems);
    const activeResizeRef = useRef(activeResize);
    const activeRotationRef = useRef(activeRotation);
    const resizeRafRef = useRef(null);
    
    useEffect(() => {
        canvasItemsRef.current = canvasItems;
    }, [canvasItems]);
    
    useEffect(() => {
        activeResizeRef.current = activeResize;
    }, [activeResize]);
    
    useEffect(() => {
        activeRotationRef.current = activeRotation;
    }, [activeRotation]);

    // Filter wardrobe items - only show user's actual wardrobe items
    const filteredWardrobeItems = wardrobeItems.filter(item => {
        const itemCategory = item.primary_category || item.display_category || item.category || '';
        const matchesCategory = selectedCategory === 'all' || 
            itemCategory.toLowerCase() === selectedCategory.toLowerCase() ||
            item.category?.toLowerCase() === selectedCategory.toLowerCase();
        const matchesSearch = !wardrobeSearch || 
            item.name?.toLowerCase().includes(wardrobeSearch.toLowerCase()) ||
            item.brand?.toLowerCase().includes(wardrobeSearch.toLowerCase()) ||
            item.color?.toLowerCase().includes(wardrobeSearch.toLowerCase());
        return matchesCategory && matchesSearch;
    });

    // Load wardrobe items: read from MyWardrobe's IndexedDB cache first (instant),
    // then refresh from API in background so data stays current.
    useEffect(() => {
        let cancelled = false;
        const currentToken = token || localStorage.getItem('token');

        const loadWardrobe = async () => {
            // Step 1: Instant load from cache (same key MyWardrobe writes to)
            try {
                const cached = await cacheGet('wardrobe_data');
                if (!cancelled && cached?.data?.items?.length) {
                    setWardrobeItems(cached.data.items);
                    setLoadingWardrobe(false);
                }
            } catch (e) {
                console.warn('Cache read failed:', e.message);
            }

            // Step 2: Refresh from API in background (if we have a token)
            if (!currentToken) { setLoadingWardrobe(false); return; }
            try {
                const res = await axios.get(`${API_URL}/real-wardrobe`, {
                    headers: { Authorization: `Bearer ${currentToken}` },
                    timeout: 15000,
                });
                if (!cancelled) {
                    setWardrobeItems(res.data.items || []);
                }
            } catch (err) {
                console.error('Failed to refresh wardrobe from API:', err.message);
                // Cache data is already shown — no action needed
            } finally {
                if (!cancelled) setLoadingWardrobe(false);
            }
        };

        loadWardrobe();
        return () => { cancelled = true; };
    }, [token]);

    // Load stylebooks for save modal
    useEffect(() => {
        const loadStylebooks = async () => {
            if (!token) return;
            try {
                const res = await axios.get(`${API_URL}/lookbooks`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setStylebooks(res.data.lookbooks || []);
            } catch (err) {
                console.error('Failed to load stylebooks:', err);
            }
        };
        loadStylebooks();
    }, [token]);

    // Load saved outfits from API (with one-time localStorage migration)
    useEffect(() => {
        const loadSavedOutfits = async () => {
            if (!token) return;
            setLoadingSavedOutfits(true);
            try {
                // Check for localStorage data to migrate
                const localData = localStorage.getItem('styleStudioOutfits');
                if (localData) {
                    try {
                        const localOutfits = JSON.parse(localData);
                        if (localOutfits.length > 0) {
                            console.log(`🔄 Migrating ${localOutfits.length} Style Studio outfits to cloud...`);
                            await axios.post(`${API_URL}/style-studio/outfits/migrate`, { outfits: localOutfits }, {
                                headers: { Authorization: `Bearer ${token}` }
                            });
                            console.log('✅ Style Studio outfits migrated to cloud');
                        }
                    } catch (migErr) {
                        console.warn('⚠️ Migration failed, data preserved locally:', migErr.message);
                    }
                    localStorage.removeItem('styleStudioOutfits');
                }

                // Fetch from API
                const res = await axios.get(`${API_URL}/style-studio/outfits`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setSavedOutfits(res.data.outfits || []);
            } catch (err) {
                console.error('Failed to load saved outfits:', err);
            } finally {
                setLoadingSavedOutfits(false);
            }
        };
        loadSavedOutfits();
    }, [token]);

    // Load saved model on mount
    useEffect(() => {
        const loadSavedModel = async () => {
            if (!token) {
                setIsLoadingModel(false);
                return;
            }
            try {
                const res = await axios.get(`${API_URL}/tryon/model`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                if (res.data.hasModel && res.data.model) {
                    const model = res.data.model;
                    setHasSavedModel(true);
                    if (model.photo_base64) {
                        const preview = `data:image/jpeg;base64,${model.photo_base64}`;
                        setUserPhotoPreview(preview);
                        setUserPhoto(model.photo_base64);
                    } else if (model.photo_url) {
                        setUserPhotoPreview(model.photo_url);
                        setUserPhoto(model.photo_url);
                    }
                }
            } catch (err) {
                console.error('Failed to load saved model:', err);
            } finally {
                setIsLoadingModel(false);
            }
        };
        loadSavedModel();
    }, [token]);

    // Fetch user subscription tier
    useEffect(() => {
        const fetchSubscription = async () => {
            if (!token) return;
            try {
                const res = await axios.get(`${API_URL}/subscription/status`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setUserTier(res.data.tier || 'free');
            } catch (err) {
                console.error('Failed to fetch subscription:', err);
            }
        };
        fetchSubscription();
    }, [token]);

    // Fetch virtual try-on history for canvas models
    useEffect(() => {
        const fetchTryOnModels = async () => {
            if (!token) return;
            setLoadingTryOnModels(true);
            try {
                const res = await axios.get(`${API_URL}/tryon/history`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                const results = res.data.results || res.data || [];
                setTryOnModels(Array.isArray(results) ? results.slice(0, 20) : []);
            } catch (err) {
                console.error('Failed to load try-on models:', err);
            } finally {
                setLoadingTryOnModels(false);
            }
        };
        fetchTryOnModels();
    }, [token]);

    // Fetch AI Studio saved creations
    useEffect(() => {
        const fetchAiStudioItems = async () => {
            if (!token) return;
            setLoadingAiStudio(true);
            try {
                const res = await axios.get(`${API_URL}/saved`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                const allSaved = res.data.items || res.data || [];
                // Filter for AI Studio items that are images (not videos)
                const studioItems = allSaved.filter(item => 
                    item.brand === 'AI Studio' && 
                    item.image_url && 
                    !item.image_url.match(/\.(mp4|webm|mov)$/i)
                );
                setAiStudioItems(studioItems.slice(0, 30));
            } catch (err) {
                console.error('Failed to load AI Studio items:', err);
            } finally {
                setLoadingAiStudio(false);
            }
        };
        fetchAiStudioItems();
    }, [token]);

    // Fetch saved stickers from dedicated sticker API (Cloudinary-backed persistence)
    useEffect(() => {
        const fetchStickers = async () => {
            if (!token) return;
            setLoadingStickers(true);
            try {
                const res = await axios.get(`${API_URL}/style-studio/stickers`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                const stickers = (res.data.stickers || []).map(s => ({
                    ...s,
                    product_id: s.id,
                    product_name: s.name || 'Sticker',
                    brand: 'Stickers',
                }));
                setStickerItems(stickers.slice(0, 50));
            } catch (err) {
                console.error('Failed to load stickers:', err);
            } finally {
                setLoadingStickers(false);
            }
        };
        fetchStickers();
    }, [token]);

    // Animate processing steps
    useEffect(() => {
        if (isProcessing) {
            const interval = setInterval(() => {
                setProcessingStep(prev => (prev + 1) % processingSteps.length);
            }, 2500);
            return () => clearInterval(interval);
        } else {
            setProcessingStep(0);
        }
    }, [isProcessing]);

    // Add item to canvas with a free position
    const addToCanvas = (item, dropX, dropY) => {
        // Default position: center-ish with slight randomness, or use drop coords
        const x = dropX != null ? dropX : 30 + Math.random() * 30;
        const y = dropY != null ? dropY : 10 + Math.random() * 30;
        setCanvasItems(prev => {
            // Text items always go to the very front so they're immediately visible & editable
            const maxZ = prev.length > 0 ? Math.max(...prev.map(i => i.zIndex || 0)) : 0;
            const newZ = item.isText ? maxZ + 10 : prev.length + 1;
            const newItems = [...prev, { 
                ...item, 
                canvasId: `${item.id}-${Date.now()}`,
                x, // percent from left
                y, // percent from top  
                w: 140, // default width in px
                rotation: 0,
                zIndex: newZ
            }];
            toast({
                description: `✅ Added ${item.name || item.category || 'item'} · ${newItems.length} on canvas`,
                duration: 1500,
            });
            return newItems;
        });
        setError(null);
    };

    // Remove item from canvas
    const removeFromCanvas = (canvasId) => {
        setCanvasItems(prev => prev.filter(item => item.canvasId !== canvasId));
        if (selectedCanvasItem === canvasId) setSelectedCanvasItem(null);
        if (editingTextItem === canvasId) setEditingTextItem(null);
    };

    // Upload photo, remove background, add to canvas (Premium feature)
    const handlePhotoUploadForCanvas = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        e.target.value = ''; // reset input
        
        if (!isPremiumUser) return;
        
        setIsUploadingPhoto(true);
        setRemovingBg(true);
        try {
            const reader = new FileReader();
            const base64 = await new Promise((resolve) => {
                reader.onload = (ev) => resolve(ev.target.result);
                reader.readAsDataURL(file);
            });
            
            // Call bg removal API
            const res = await axios.post(`${API_URL}/real-wardrobe/remove-background`, 
                { imageBase64: base64.split(',')[1] },
                { headers: { Authorization: `Bearer ${token}` }, timeout: 120000 }
            );
            
            const processedImage = res.data.processedImage || res.data.image;
            if (processedImage) {
                // Add to canvas as a special photo item
                const photoItem = {
                    id: `photo-${Date.now()}`,
                    name: 'Uploaded Photo',
                    image_url: processedImage,
                    isUploadedPhoto: true,
                };
                addToCanvas(photoItem);
            }
        } catch (err) {
            console.error('Background removal failed:', err);
            setError('Failed to process photo. Please try again.');
        } finally {
            setIsUploadingPhoto(false);
            setRemovingBg(false);
        }
    };

    // Upload photo as-is (no background removal)
    const handleRawPhotoUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        e.target.value = '';
        if (!isPremiumUser) return;

        setIsUploadingPhoto(true);
        try {
            const reader = new FileReader();
            const base64 = await new Promise((resolve) => {
                reader.onload = (ev) => resolve(ev.target.result);
                reader.readAsDataURL(file);
            });

            // Upload to Cloudinary via backend
            const res = await axios.post(`${API_URL}/style-studio/upload-background`, {
                imageUrl: base64,
            }, { headers: { Authorization: `Bearer ${token}` } });

            const photoItem = {
                id: `photo-${Date.now()}`,
                name: 'Uploaded Photo',
                image_url: res.data.url || base64,
                isUploadedPhoto: true,
            };
            addToCanvas(photoItem);
        } catch (err) {
            console.error('Photo upload failed:', err);
            setError('Failed to upload photo. Please try again.');
        } finally {
            setIsUploadingPhoto(false);
        }
    };

    // Upload custom background image (Premium feature)
    const handleBgImageUpload = (e) => {
        const file = e.target.files?.[0];
        if (!file || !isPremiumUser) return;
        e.target.value = '';
        
        const reader = new FileReader();
        reader.onload = (ev) => {
            setBoardBgImage(ev.target.result);
        };
        reader.readAsDataURL(file);
    };

    // Add a virtual try-on result to canvas
    const addTryOnModelToCanvas = (model) => {
        const modelItem = {
            id: `tryon-${model.id || Date.now()}`,
            name: 'Virtual Try-On',
            image_url: model.result_image_url,
            isTryOnModel: true,
        };
        addToCanvas(modelItem);
    };

    // Add an AI Studio creation to canvas
    const addAiStudioToCanvas = (item) => {
        const studioItem = {
            id: `ai-studio-${item.product_id || Date.now()}`,
            name: item.product_name || 'AI Studio',
            image_url: item.image_url,
            isAiStudio: true,
        };
        addToCanvas(studioItem);
    };

    // Add a saved sticker to canvas
    const addStickerToCanvas = (item) => {
        const stickerItem = {
            id: `sticker-${item.id || Date.now()}`,
            name: 'Sticker',
            image_url: item.image_url,
            isSticker: true,
        };
        addToCanvas(stickerItem);
    };
    
    // Add a shape to canvas
    const addShapeToCanvas = (shapeType) => {
        const shapeItem = {
            id: `shape-${Date.now()}`,
            name: `${shapeType.charAt(0).toUpperCase() + shapeType.slice(1)} Shape`,
            shapeType: shapeType,
            color: shapeColor,
            strokeWidth: shapeStrokeWidth,
            isShape: true,
            locked: false,
        };
        addToCanvas(shapeItem);
    };

    // Add text to canvas (FREE feature)
    const addTextToCanvas = () => {
        const textItem = {
            id: `text-${Date.now()}`,
            name: 'Text',
            isText: true,
            textContent: 'Tap to edit',
            fontFamily: 'Inter',
            fontSize: 18,
            fontColor: '#000000',
            fontWeight: 'normal',
            fontStyle: 'normal',
            locked: false,
        };
        addToCanvas(textItem);
    };

    // Paste image from clipboard (iOS: copy subject from Photos → paste here)
    const pasteFromClipboard = async () => {
        if (isPasting) return;
        setIsPasting(true);
        try {
            if (!navigator.clipboard || !navigator.clipboard.read) {
                setError('Clipboard not supported. Copy an image and try again.');
                return;
            }

            const clipboardItems = await navigator.clipboard.read();
            let imageBlob = null;

            for (const item of clipboardItems) {
                const imageType = item.types.find(t => t.startsWith('image/'));
                if (imageType) {
                    imageBlob = await item.getType(imageType);
                    break;
                }
            }

            if (!imageBlob) {
                setError('No image found in clipboard. Copy a subject from Photos first!');
                return;
            }

            // Convert blob to base64 data URL
            const reader = new FileReader();
            const dataUrl = await new Promise((resolve, reject) => {
                reader.onload = (e) => resolve(e.target.result);
                reader.onerror = reject;
                reader.readAsDataURL(imageBlob);
            });

            const timestamp = Date.now();
            const stickerName = `Sticker ${new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
            const stickerId = `sticker-${timestamp}`;

            // Save to dedicated sticker API (uploads to Cloudinary for persistence)
            let savedSticker = null;
            try {
                const saveRes = await axios.post(`${API_URL}/style-studio/stickers`, {
                    imageUrl: dataUrl,
                    name: stickerName,
                    category: 'uploaded',
                }, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                savedSticker = saveRes.data.sticker;
            } catch (saveErr) {
                console.error('❌ Failed to save sticker:', saveErr);
            }

            // Add to local stickers state (use Cloudinary URL if available)
            setStickerItems(prev => [{
                product_id: savedSticker?.id || stickerId,
                product_name: stickerName,
                brand: 'Stickers',
                image_url: savedSticker?.image_url || dataUrl,
                id: savedSticker?.id || stickerId,
            }, ...prev]);

            // Add to canvas (use Cloudinary URL if upload succeeded, otherwise base64)
            addToCanvas({
                id: savedSticker?.id || stickerId,
                name: stickerName,
                image_url: savedSticker?.image_url || dataUrl,
                isSticker: true,
            });

            // Switch to stickers tab
            setSelectedCategory('stickers');
            setError(null);

        } catch (err) {
            console.error('❌ Clipboard paste failed:', err);
            if (err.name === 'NotAllowedError') {
                setError('Clipboard access denied. Please allow clipboard access and try again.');
            } else {
                setError('Failed to paste. Copy an image from Photos first, then tap Paste.');
            }
        } finally {
            setIsPasting(false);
        }
    };

    // Clear canvas
    const clearCanvas = () => {
        setCanvasItems([]);
        setBoardBgImage(null);
        setOutfitName('');
        setOutfitCategory('');
        setBoardBg('#ffffff');
        setShowDots(true);
        setSelectedCanvasItem(null);
        setEditingTextItem(null);
        setEditingOutfitId(null);
    };

    // Delete a saved outfit
    const deleteOutfit = async (outfitId, e) => {
        e.stopPropagation();
        try {
            await axios.delete(`${API_URL}/style-studio/outfits/${outfitId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setSavedOutfits(prev => prev.filter(o => o.id !== outfitId));
            if (editingOutfitId === outfitId) setEditingOutfitId(null);
            toast({ title: 'Outfit deleted' });
        } catch (err) {
            console.error('Failed to delete outfit:', err);
            toast({ title: 'Error', description: 'Failed to delete outfit', variant: 'destructive' });
        }
    };

    // Layer controls
    const bringToFront = (canvasId) => {
        setCanvasItems(prev => {
            const maxZ = Math.max(...prev.map(i => i.zIndex || 0));
            return prev.map(i => i.canvasId === canvasId ? { ...i, zIndex: maxZ + 1 } : i);
        });
    };

    const sendToBack = (canvasId) => {
        setCanvasItems(prev => {
            const minZ = Math.min(...prev.map(i => i.zIndex || 0));
            return prev.map(i => i.canvasId === canvasId ? { ...i, zIndex: minZ - 1 } : i);
        });
    };

    const bringForward = (canvasId) => {
        setCanvasItems(prev => {
            const sorted = [...prev].sort((a, b) => (a.zIndex || 0) - (b.zIndex || 0));
            const idx = sorted.findIndex(i => i.canvasId === canvasId);
            if (idx < sorted.length - 1) {
                const myZ = sorted[idx].zIndex || 0;
                const aboveZ = sorted[idx + 1].zIndex || 0;
                const aboveId = sorted[idx + 1].canvasId;
                return prev.map(i => {
                    if (i.canvasId === canvasId) return { ...i, zIndex: aboveZ };
                    if (i.canvasId === aboveId) return { ...i, zIndex: myZ };
                    return i;
                });
            }
            return prev;
        });
    };

    const sendBackward = (canvasId) => {
        setCanvasItems(prev => {
            const sorted = [...prev].sort((a, b) => (a.zIndex || 0) - (b.zIndex || 0));
            const idx = sorted.findIndex(i => i.canvasId === canvasId);
            if (idx > 0) {
                const myZ = sorted[idx].zIndex || 0;
                const belowZ = sorted[idx - 1].zIndex || 0;
                const belowId = sorted[idx - 1].canvasId;
                return prev.map(i => {
                    if (i.canvasId === canvasId) return { ...i, zIndex: belowZ };
                    if (i.canvasId === belowId) return { ...i, zIndex: myZ };
                    return i;
                });
            }
            return prev;
        });
    };

    // Resize a canvas item via corner drag
    const handleResizePointerDown = useCallback((e, canvasId) => {
        e.stopPropagation();
        e.preventDefault();
        const item = canvasItemsRef.current.find(i => i.canvasId === canvasId);
        if (!item) return;
        const state = { canvasId, startX: e.clientX, startW: item.w || 140 };
        activeResizeRef.current = state;
        setActiveResize(state);
    }, []);

    const handleResizePointerMove = useCallback((e) => {
        if (!activeResizeRef.current) return;
        try {
            const delta = e.clientX - activeResizeRef.current.startX;
            const newW = Math.max(50, Math.min(400, (activeResizeRef.current.startW || 140) + delta));
            if (isNaN(newW) || !isFinite(newW)) return;
            setCanvasItems(prev => prev.map(i =>
                i.canvasId === activeResizeRef.current?.canvasId ? { ...i, w: newW } : i
            ));
        } catch (err) {
            console.warn('Resize error:', err);
            setActiveResize(null);
        }
    }, []);

    const handleResizePointerUp = useCallback(() => {
        setActiveResize(null);
    }, []);

    useEffect(() => {
        if (activeResize) {
            const onTouchMove = (e) => {
                if (e.touches.length === 1) {
                    e.preventDefault();
                    handleResizePointerMove({ clientX: e.touches[0].clientX, clientY: e.touches[0].clientY });
                }
            };
            const onTouchEnd = () => handleResizePointerUp();
            window.addEventListener('pointermove', handleResizePointerMove);
            window.addEventListener('pointerup', handleResizePointerUp);
            window.addEventListener('pointercancel', handleResizePointerUp);
            window.addEventListener('touchmove', onTouchMove, { passive: false });
            window.addEventListener('touchend', onTouchEnd);
            window.addEventListener('touchcancel', onTouchEnd);
            return () => {
                window.removeEventListener('pointermove', handleResizePointerMove);
                window.removeEventListener('pointerup', handleResizePointerUp);
                window.removeEventListener('pointercancel', handleResizePointerUp);
                window.removeEventListener('touchmove', onTouchMove);
                window.removeEventListener('touchend', onTouchEnd);
                window.removeEventListener('touchcancel', onTouchEnd);
            };
        }
    }, [activeResize, handleResizePointerMove, handleResizePointerUp]);

    // Height resize for shapes via bottom-center handle
    const [activeHeightResize, setActiveHeightResize] = useState(null);
    const activeHeightResizeRef = useRef(null);
    const heightRafRef = useRef(null);

    const handleHeightResizePointerDown = useCallback((e, canvasId) => {
        e.stopPropagation();
        e.preventDefault();
        const item = canvasItemsRef.current.find(i => i.canvasId === canvasId);
        if (!item) return;
        const state = { canvasId, startY: e.clientY, startH: item.h || item.w || 140 };
        activeHeightResizeRef.current = state;
        setActiveHeightResize(state);
    }, []);

    const handleHeightResizePointerMove = useCallback((e) => {
        if (!activeHeightResizeRef.current) return;
        const delta = e.clientY - activeHeightResizeRef.current.startY;
        const newH = Math.max(20, Math.min(500, activeHeightResizeRef.current.startH + delta));
        if (isNaN(newH) || !isFinite(newH)) return;
        setCanvasItems(prev => prev.map(i =>
            i.canvasId === activeHeightResizeRef.current?.canvasId ? { ...i, h: newH } : i
        ));
    }, []);

    const handleHeightResizePointerUp = useCallback(() => {
        activeHeightResizeRef.current = null;
        setActiveHeightResize(null);
    }, []);

    useEffect(() => {
        if (activeHeightResize) {
            const onTouchMove = (e) => {
                if (e.touches.length === 1) {
                    e.preventDefault();
                    handleHeightResizePointerMove({ clientY: e.touches[0].clientY });
                }
            };
            const onTouchEnd = () => handleHeightResizePointerUp();
            window.addEventListener('pointermove', handleHeightResizePointerMove);
            window.addEventListener('pointerup', handleHeightResizePointerUp);
            window.addEventListener('pointercancel', handleHeightResizePointerUp);
            window.addEventListener('touchmove', onTouchMove, { passive: false });
            window.addEventListener('touchend', onTouchEnd);
            window.addEventListener('touchcancel', onTouchEnd);
            return () => {
                window.removeEventListener('pointermove', handleHeightResizePointerMove);
                window.removeEventListener('pointerup', handleHeightResizePointerUp);
                window.removeEventListener('pointercancel', handleHeightResizePointerUp);
                window.removeEventListener('touchmove', onTouchMove);
                window.removeEventListener('touchend', onTouchEnd);
                window.removeEventListener('touchcancel', onTouchEnd);
            };
        }
    }, [activeHeightResize, handleHeightResizePointerMove, handleHeightResizePointerUp]);

    // Rotation via corner handle drag
    const handleRotatePointerDown = useCallback((e, canvasId) => {
        e.stopPropagation();
        e.preventDefault();
        const item = canvasItemsRef.current.find(i => i.canvasId === canvasId);
        if (!item || !canvasRef.current) return;
        const rect = canvasRef.current.getBoundingClientRect();
        // Calculate item center in viewport coords
        const itemW = item.w || 140;
        const itemLeftPx = (item.x / 100) * rect.width;
        const el = e.target.closest('[data-canvas-item]');
        const itemH = el ? el.offsetHeight : itemW;
        const centerX = rect.left + itemLeftPx + itemW / 2;
        const centerY = rect.top + (item.y / 100) * rect.height + itemH / 2;
        const startAngle = Math.atan2(e.clientY - centerY, e.clientX - centerX) * (180 / Math.PI);
        setActiveRotation({ canvasId, centerX, centerY, startAngle, startRotation: item.rotation || 0 });
    }, []);

    const handleRotatePointerMove = useCallback((e) => {
        try {
            if (!activeRotationRef.current) return;
            const currentAngle = Math.atan2(e.clientY - activeRotationRef.current.centerY, e.clientX - activeRotationRef.current.centerX) * (180 / Math.PI);
            let delta = currentAngle - activeRotationRef.current.startAngle;
            let newRotation = activeRotationRef.current.startRotation + delta;
            if (isNaN(newRotation) || !isFinite(newRotation)) return;
            // Snap to 15° increments when within 5° tolerance
            const snapAngle = Math.round(newRotation / 15) * 15;
            if (Math.abs(newRotation - snapAngle) < 5) newRotation = snapAngle;
            setCanvasItems(prev => prev.map(i =>
                i.canvasId === activeRotationRef.current?.canvasId ? { ...i, rotation: newRotation } : i
            ));
        } catch (err) {
            console.warn('Rotate error:', err);
            setActiveRotation(null);
        }
    }, []);

    const handleRotatePointerUp = useCallback(() => {
        setActiveRotation(null);
    }, []);

    useEffect(() => {
        if (activeRotation) {
            const onTouchMove = (e) => {
                if (e.touches.length === 1) {
                    e.preventDefault();
                    handleRotatePointerMove({ clientX: e.touches[0].clientX, clientY: e.touches[0].clientY });
                }
            };
            const onTouchEnd = () => handleRotatePointerUp();
            window.addEventListener('pointermove', handleRotatePointerMove);
            window.addEventListener('pointerup', handleRotatePointerUp);
            window.addEventListener('pointercancel', handleRotatePointerUp);
            window.addEventListener('touchmove', onTouchMove, { passive: false });
            window.addEventListener('touchend', onTouchEnd);
            window.addEventListener('touchcancel', onTouchEnd);
            return () => {
                window.removeEventListener('pointermove', handleRotatePointerMove);
                window.removeEventListener('pointerup', handleRotatePointerUp);
                window.removeEventListener('pointercancel', handleRotatePointerUp);
                window.removeEventListener('touchmove', onTouchMove);
                window.removeEventListener('touchend', onTouchEnd);
                window.removeEventListener('touchcancel', onTouchEnd);
            };
        }
    }, [activeRotation, handleRotatePointerMove, handleRotatePointerUp]);

    // Safety timeout: auto-release any stuck drag/resize/rotate state after 8 seconds
    // Prevents the page from getting stuck if pointerup is missed (common on iOS)
    useEffect(() => {
        if (activeCanvasDrag || activeResize || activeRotation) {
            const safetyTimer = setTimeout(() => {
                if (activeCanvasDrag) setActiveCanvasDrag(null);
                if (activeResize) setActiveResize(null);
                if (activeRotation) setActiveRotation(null);
            }, 8000);
            return () => clearTimeout(safetyTimer);
        }
    }, [activeCanvasDrag, activeResize, activeRotation]);

    // Handle drag start from wardrobe sidebar
    const handleDragStart = (e, item) => {
        setDraggingItem(item);
        e.dataTransfer.setData('text/plain', JSON.stringify(item));
        e.dataTransfer.effectAllowed = 'copy';
    };

    // Handle drop from wardrobe onto canvas — place at drop position
    const handleCanvasDrop = (e) => {
        e.preventDefault();
        setDragActive(false);
        try {
            const itemData = e.dataTransfer.getData('text/plain');
            if (itemData && canvasRef.current) {
                const item = JSON.parse(itemData);
                const rect = canvasRef.current.getBoundingClientRect();
                const xPct = ((e.clientX - rect.left - 70) / rect.width) * 100;
                const yPct = ((e.clientY - rect.top - 70) / rect.height) * 100;
                addToCanvas(item, Math.max(0, Math.min(85, xPct)), Math.max(0, Math.min(85, yPct)));
            }
        } catch (err) {
            console.error('Drop error:', err);
        }
        setDraggingItem(null);
    };

    const handleCanvasDragOver = (e) => {
        e.preventDefault();
        setDragActive(true);
    };

    const handleCanvasDragLeave = () => {
        setDragActive(false);
    };

    // --- Canvas item repositioning via pointer events ---
    const handleCanvasItemPointerDown = useCallback((e, canvasId) => {
        if (!canvasEditMode) return; // Locked — don't allow dragging
        // Clear any editing state (the <p>'s own stopPropagation prevents
        // this from firing when user touches INSIDE the text during editing)
        if (editingTextItem) setEditingTextItem(null);
        e.stopPropagation();
        if (!canvasRef.current) return;
        const rect = canvasRef.current.getBoundingClientRect();
        const item = canvasItemsRef.current.find(i => i.canvasId === canvasId);
        if (!item) return;
        const itemLeftPx = (item.x / 100) * rect.width;
        const itemTopPx = (item.y / 100) * rect.height;
        setActiveCanvasDrag({
            canvasId,
            offsetX: e.clientX - rect.left - itemLeftPx,
            offsetY: e.clientY - rect.top - itemTopPx
        });
        bringToFront(canvasId);
        setSelectedCanvasItem(canvasId);
    }, [canvasEditMode, editingTextItem]);

    const handleCanvasPointerMove = useCallback((e) => {
        try {
            if (!activeCanvasDrag || !canvasRef.current) return;
            const rect = canvasRef.current.getBoundingClientRect();
            if (!rect.width || !rect.height) return;
            const newXPx = e.clientX - rect.left - activeCanvasDrag.offsetX;
            const newYPx = e.clientY - rect.top - activeCanvasDrag.offsetY;
            const xPct = (newXPx / rect.width) * 100;
            const yPct = (newYPx / rect.height) * 100;
            if (isNaN(xPct) || isNaN(yPct) || !isFinite(xPct) || !isFinite(yPct)) return;
            setCanvasItems(prev => prev.map(i =>
                i.canvasId === activeCanvasDrag.canvasId
                    ? { ...i, x: xPct, y: yPct }
                    : i
            ));
        } catch (err) {
            console.warn('Drag error:', err);
            setActiveCanvasDrag(null);
        }
    }, [activeCanvasDrag]);

    const handleCanvasPointerUp = useCallback(() => {
        setActiveCanvasDrag(null);
    }, []);

    // Attach/detach pointer + touch listeners for canvas item repositioning
    // useLayoutEffect ensures listeners attach synchronously after state change,
    // before the browser fires the next touchmove/pointermove.
    useLayoutEffect(() => {
        if (activeCanvasDrag) {
            const onTouchMove = (e) => {
                if (e.touches.length === 1) {
                    e.preventDefault();
                    handleCanvasPointerMove({ clientX: e.touches[0].clientX, clientY: e.touches[0].clientY });
                }
            };
            const onTouchEnd = () => handleCanvasPointerUp();
            window.addEventListener('pointermove', handleCanvasPointerMove);
            window.addEventListener('pointerup', handleCanvasPointerUp);
            window.addEventListener('pointercancel', handleCanvasPointerUp);
            window.addEventListener('touchmove', onTouchMove, { passive: false });
            window.addEventListener('touchend', onTouchEnd);
            window.addEventListener('touchcancel', onTouchEnd);
            return () => {
                window.removeEventListener('pointermove', handleCanvasPointerMove);
                window.removeEventListener('pointerup', handleCanvasPointerUp);
                window.removeEventListener('pointercancel', handleCanvasPointerUp);
                window.removeEventListener('touchmove', onTouchMove);
                window.removeEventListener('touchend', onTouchEnd);
                window.removeEventListener('touchcancel', onTouchEnd);
            };
        }
    }, [activeCanvasDrag, handleCanvasPointerMove, handleCanvasPointerUp]);

    // Save outfit
    const saveOutfit = async () => {
        if (canvasItems.length === 0) {
            setError('Add items to your outfit first');
            return;
        }
        if (!saveOptions.toStylebook && !saveOptions.toStudioSaves && !saveOptions.toSavedLooks) {
            setError('Select at least one save destination');
            return;
        }

        setIsSaving(true);
        setError(null);

        try {
            // Upload any sticker base64 images to Cloudinary before saving (with retry)
            let uploadFailed = false;
            const processedItems = await Promise.all(canvasItems.map(async (item) => {
                if (item.isSticker && item.image_url && item.image_url.startsWith('data:')) {
                    // Try up to 3 times
                    for (let attempt = 1; attempt <= 3; attempt++) {
                        try {
                            const uploadRes = await axios.post(`${API_URL}/style-studio/stickers`, {
                                imageUrl: item.image_url,
                                name: item.name || 'Sticker',
                            }, { headers: { Authorization: `Bearer ${token}` } });
                            const cloudUrl = uploadRes.data.sticker?.image_url;
                            if (cloudUrl) return { ...item, image_url: cloudUrl };
                        } catch (e) {
                            console.warn(`Sticker upload attempt ${attempt}/3 failed:`, e.message);
                            if (attempt < 3) await new Promise(r => setTimeout(r, 1000 * attempt));
                        }
                    }
                    // All retries failed — flag it
                    uploadFailed = true;
                    console.error('Sticker upload failed after 3 attempts:', item.name);
                }
                return item;
            }));

            if (uploadFailed) {
                setError('Some stickers could not be saved. Please check your connection and try again.');
                setIsSaving(false);
                return;
            }

            // Strip heavy base64 data from items before saving — only keep what we need
            const lightItems = processedItems.map(item => ({
                id: item.id,
                canvasId: item.canvasId,
                name: item.name,
                category: item.category,
                primary_category: item.primary_category,
                display_category: item.display_category,
                color: item.color,
                brand: item.brand,
                // For image: prefer a short URL; stickers/shapes handled above
                image_url: item.image_url && !item.image_url.startsWith('data:') 
                    ? item.image_url 
                    : (item.original_image_url && !item.original_image_url.startsWith('data:') 
                        ? item.original_image_url 
                        : undefined),
                image: item.image && !item.image.startsWith('data:') ? item.image : undefined,
                // Shape properties — must persist for shapes to render on reload
                isShape: item.isShape || false,
                shapeType: item.shapeType || null,
                strokeWidth: item.strokeWidth || 6,
                locked: item.locked || false,
                // Sticker properties
                isSticker: item.isSticker || false,
                // Text properties
                isText: item.isText || false,
                textContent: item.textContent || '',
                fontFamily: item.fontFamily || 'Inter',
                fontSize: item.fontSize || 18,
                fontColor: item.fontColor || '#000000',
                fontWeight: item.fontWeight || 'normal',
                fontStyle: item.fontStyle || 'normal',
                // Whiteboard position
                x: item.x,
                y: item.y,
                w: item.w,
                h: item.h || undefined,
                rotation: item.rotation || 0,
                zIndex: item.zIndex
            }));

            // Capture canvas screenshot as thumbnail
            let canvasThumbnail = null;
            if (canvasRef.current) {
                try {
                    const prevSelected = selectedCanvasItem;
                    setSelectedCanvasItem(null);
                    setEditingTextItem(null);
                    await new Promise(r => setTimeout(r, 100));
                    const captured = await html2canvas(canvasRef.current, {
                        backgroundColor: boardBg || '#ffffff',
                        scale: 1,
                        useCORS: true,
                        allowTaint: true,
                        logging: false,
                    });
                    setSelectedCanvasItem(prevSelected);
                    canvasThumbnail = captured.toDataURL('image/jpeg', 0.7);
                } catch (thumbErr) {
                    console.warn('Thumbnail capture failed:', thumbErr.message);
                }
            }

            const outfit = {
                id: editingOutfitId || `outfit-${Date.now()}`,
                name: outfitName || `Outfit ${new Date().toLocaleDateString()}`,
                category: outfitCategory || 'Uncategorized',
                boardBg: boardBg || '#ffffff',
                boardBgImage: null, // will be set below if exists
                showDots: showDots,
                items: lightItems,
                itemIds: canvasItems.map(i => i.id),
                createdAt: editingOutfitId 
                    ? (savedOutfits.find(o => o.id === editingOutfitId)?.createdAt || new Date().toISOString())
                    : new Date().toISOString(),
                updatedAt: new Date().toISOString(),
                thumbnail: canvasThumbnail || lightItems[0]?.image_url || lightItems[0]?.image
            };

            // Upload background image to Cloudinary if present
            if (boardBgImage) {
                try {
                    if (boardBgImage.startsWith('data:')) {
                        const bgRes = await axios.post(`${API_URL}/style-studio/upload-background`, {
                            imageUrl: boardBgImage,
                        }, { headers: { Authorization: `Bearer ${token}` } });
                        outfit.boardBgImage = bgRes.data.url;
                    } else {
                        // Already a URL (Cloudinary), keep as-is
                        outfit.boardBgImage = boardBgImage;
                    }
                } catch (bgErr) {
                    console.warn('Failed to upload background to Cloudinary:', bgErr.message);
                }
            }

            // Save to Style Studio (cloud)
            if (saveOptions.toStudioSaves) {
                try {
                    const payload = {
                        id: editingOutfitId || undefined,
                        clientId: outfit.id,
                        name: outfit.name,
                        category: outfit.category,
                        boardBg: outfit.boardBg,
                        boardBgImage: outfit.boardBgImage,
                        showDots: outfit.showDots,
                        items: outfit.items,
                        itemIds: outfit.itemIds,
                        thumbnail: outfit.thumbnail
                    };
                    const saveRes = await axios.post(`${API_URL}/style-studio/outfits`, payload, {
                        headers: { Authorization: `Bearer ${token}` }
                    });
                    const savedOutfit = saveRes.data.outfit;
                    if (editingOutfitId) {
                        setSavedOutfits(prev => prev.map(o => o.id === editingOutfitId ? savedOutfit : o));
                    } else {
                        setSavedOutfits(prev => [savedOutfit, ...prev].slice(0, 50));
                    }
                } catch (saveErr) {
                    console.error('Failed to save outfit to cloud:', saveErr.message);
                    toast({ title: 'Warning', description: 'Outfit may not have saved to cloud', variant: 'destructive' });
                }
            }

            // Save to Stylebook (Lookbook)
            if (saveOptions.toStylebook) {
                let targetStylebookId = saveOptions.stylebookId;

                // Create a new stylebook if user chose to
                if (saveOptions.stylebookId === '__new__' && saveOptions.newStylebookName) {
                    try {
                        const createRes = await axios.post(`${API_URL}/lookbooks`, {
                            name: saveOptions.newStylebookName,
                            description: `Created from Style Studio: ${outfit.name}`,
                            style: 'casual',
                        }, {
                            headers: { Authorization: `Bearer ${token}` }
                        });
                        targetStylebookId = createRes.data.lookbook?.id;
                    } catch (createErr) {
                        console.warn('Failed to create new stylebook:', createErr.message);
                        targetStylebookId = null;
                    }
                }

                if (targetStylebookId && targetStylebookId !== '__new__') {
                    for (const item of canvasItems) {
                        if (item.isShape || item.isSticker || item.isText) continue; // Skip shapes, stickers & text
                        const imgUrl = item.image_url || item.image || item.original_image_url || '';
                        // Always include customImageUrl as fallback so the item has a visible image
                        const payload = typeof item.id === 'number'
                            ? { wardrobeItemId: item.id, customImageUrl: imgUrl, customTitle: item.name, notes: `Part of outfit: ${outfit.name}` }
                            : { customImageUrl: imgUrl, customTitle: item.name || 'Outfit piece', notes: `Part of outfit: ${outfit.name}` };

                        await axios.post(`${API_URL}/lookbooks/${targetStylebookId}/items`, payload, {
                            headers: { Authorization: `Bearer ${token}` }
                        }).catch(err => {
                            console.warn('Failed to save item to stylebook:', err.message);
                            // Retry with just image
                            return axios.post(`${API_URL}/lookbooks/${targetStylebookId}/items`, {
                                customImageUrl: imgUrl,
                                customTitle: item.name || 'Outfit piece',
                                notes: `Part of outfit: ${outfit.name}`,
                            }, {
                                headers: { Authorization: `Bearer ${token}` }
                            }).catch(() => {});
                        });
                    }
                }
            }

            // Save to Saved Looks (backend /api/saved — visible in the Saved page)
            if (saveOptions.toSavedLooks) {
                try {
                    // Capture full canvas as screenshot so the saved look shows the whole board
                    let canvasImageUrl = '';
                    if (canvasRef.current) {
                        const prevSelected = selectedCanvasItem;
                        setSelectedCanvasItem(null);
                        setEditingTextItem(null);
                        await new Promise(r => setTimeout(r, 100));
                        const captured = await html2canvas(canvasRef.current, {
                            backgroundColor: boardBg || '#ffffff',
                            scale: 2,
                            useCORS: true,
                            allowTaint: true,
                            logging: false,
                        });
                        setSelectedCanvasItem(prevSelected);
                        canvasImageUrl = captured.toDataURL('image/png');
                    }

                    if (canvasImageUrl) {
                        await axios.post(`${API_URL}/saved`, {
                            product: {
                                name: outfit.name || `Style Studio Look`,
                                brand: 'AI Studio',
                                image_url: canvasImageUrl,
                                category: outfit.category || 'Outfit',
                                style: 'outfit-board',
                            },
                            source: 'ai-studio'
                        }, {
                            headers: { Authorization: `Bearer ${token}` }
                        });
                    }
                } catch (err) {
                    console.warn('Could not save to Saved Looks:', err.message);
                }
            }

            setSaveSuccess(true);
            setTimeout(() => {
                setShowSaveModal(false);
                setSaveSuccess(false);
                clearCanvas();
            }, 1500);
        } catch (err) {
            console.error('Save error:', err);
            setError('Failed to save outfit');
        } finally {
            setIsSaving(false);
        }
    };

    // Download canvas board as image to device gallery
    const downloadCanvasImage = async () => {
        if (!canvasRef.current || canvasItems.length === 0) {
            toast({ title: 'Nothing to download', description: 'Add items to your board first' });
            return;
        }
        try {
            toast({ title: '⬇️ Capturing board...', description: 'Generating image' });

            // Temporarily hide selection rings and controls
            const prev = selectedCanvasItem;
            setSelectedCanvasItem(null);
            setEditingTextItem(null);
            await new Promise(r => setTimeout(r, 100));

            const canvas = await html2canvas(canvasRef.current, {
                backgroundColor: boardBg || '#ffffff',
                scale: 2,
                useCORS: true,
                allowTaint: true,
                logging: false,
            });

            setSelectedCanvasItem(prev);

            const dataUrl = canvas.toDataURL('image/png');
            const base64 = dataUrl.split(',')[1];
            const fileName = `style-studio-${Date.now()}.png`;

            const isNative = window.Capacitor?.isNativePlatform?.();

            if (isNative) {
                const savedFile = await Filesystem.writeFile({
                    path: fileName,
                    data: base64,
                    directory: Directory.Cache,
                });
                await Share.share({
                    title: 'Style Studio Board',
                    url: savedFile.uri,
                });
                toast({ title: '✅ Ready to save!', description: 'Use the share sheet to save to Photos' });
            } else {
                // Try Web Share API (mobile browsers — opens share sheet with Save Image option)
                try {
                    const blob = await (await fetch(dataUrl)).blob();
                    const file = new File([blob], fileName, { type: 'image/png' });
                    if (navigator.canShare && navigator.canShare({ files: [file] })) {
                        await navigator.share({ title: 'Style Studio Board', files: [file] });
                        toast({ title: '✅ Saved!', description: 'Image ready to save to Photos' });
                        return;
                    }
                } catch (shareErr) {
                    // User cancelled share or API unavailable — fall through to download
                    if (shareErr.name === 'AbortError') return;
                }
                // Desktop fallback — browser download
                const link = document.createElement('a');
                link.href = dataUrl;
                link.download = fileName;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                toast({ title: '✅ Downloaded!', description: `${fileName} saved` });
            }
        } catch (err) {
            console.error('Download failed:', err);
            toast({ title: '⚠️ Download failed', description: 'Please try again', variant: 'destructive' });
        }
    };

    // Handle file for try-on
    const handleFile = (file) => {
        if (!file.type.startsWith('image/')) {
            setError('Please upload an image file (JPG, PNG)');
            return;
        }
        if (file.size > 10 * 1024 * 1024) {
            setError('Image must be less than 10MB');
            return;
        }
        
        setError(null);
        setHasSavedModel(false);
        setTryOnResult(null);
        
        const reader = new FileReader();
        reader.onload = (event) => {
            setUserPhotoPreview(event.target.result);
            const base64 = event.target.result.split(',')[1];
            setUserPhoto(base64);
        };
        reader.readAsDataURL(file);
    };

    // Generate try-on
    const generateTryOn = async () => {
        if (!userPhoto) {
            setError('Please upload your photo first');
            return;
        }
        if (!selectedGarmentForTryon) {
            setError('Please select a garment to try on');
            return;
        }

        setIsProcessing(true);
        setError(null);

        try {
            if (!hasSavedModel) {
                await axios.post(`${API_URL}/tryon/model`, {
                    photoBase64: userPhoto,
                    consent: true
                }, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setHasSavedModel(true);
            }

            // Determine garment type from primary_category, display_category, or category
            const itemCategory = (selectedGarmentForTryon.primary_category || 
                                  selectedGarmentForTryon.display_category || 
                                  selectedGarmentForTryon.category || '').toLowerCase();
            let garmentType = 'tops';
            if (['dresses', 'one-pieces', 'jumpsuits'].includes(itemCategory)) {
                garmentType = 'one-pieces';
            } else if (['bottoms', 'pants', 'skirts', 'shorts'].includes(itemCategory)) {
                garmentType = 'bottoms';
            }

            const res = await axios.post(`${API_URL}/tryon/generate`, {
                garmentImageUrl: selectedGarmentForTryon.image_url || selectedGarmentForTryon.image,
                garmentType,
                garmentDescription: `${selectedGarmentForTryon.color || ''} ${selectedGarmentForTryon.name || ''}`.trim()
            }, {
                headers: { Authorization: `Bearer ${token}` },
                timeout: 180000
            });

            const resultUrl = res.data.resultImageUrl;
            setTryOnResult(resultUrl);
            
            const newHistoryItem = {
                id: Date.now(),
                result: resultUrl,
                garment: selectedGarmentForTryon,
                userPhoto: userPhotoPreview,
                timestamp: new Date()
            };
            setTryOnHistory(prev => [newHistoryItem, ...prev].slice(0, 20));
            
        } catch (err) {
            console.error('Try-on error:', err);
            const rawMsg = err.response?.data?.message || err.response?.data?.error || 'AI processing failed';
            const errorMsg = typeof rawMsg === 'object' ? (rawMsg.message || 'AI processing failed') : rawMsg;
            
            if (err.response?.status === 429 || err.response?.status === 403) {
                setError('⚠️ You\'ve reached your try-on limit for this month. Upgrade your plan for more try-ons!');
            } else if (errorMsg.includes('person') || errorMsg.includes('body') || errorMsg.includes('pose') || errorMsg.includes('Pose')) {
                setError('📷 Photo issue detected. For best results:\n• Use a front-facing portrait (head to hip)\n• Stand with arms relaxed at sides\n• Use even, natural lighting\n• Avoid busy backgrounds\n• Make sure the photo is at least 800×1200px');
            } else if (err.code === 'ECONNABORTED') {
                setError('⏱️ Processing timed out. Try again or use a simpler garment image.');
            } else {
                setError(`Try-on failed: ${errorMsg}`);
            }
        } finally {
            setIsProcessing(false);
        }
    };

    const downloadResult = (url) => {
        const link = document.createElement('a');
        link.download = `fashiondisc-tryon-${Date.now()}.jpg`;
        link.href = url || tryOnResult;
        link.click();
    };

    return (
        <div className="min-h-screen">
            {/* Fullscreen Modal */}
            <AnimatePresence>
                {showFullscreen && selectedHistoryItem && (
                    <motion.div 
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
                        onClick={() => setShowFullscreen(false)}
                    >
                        <button 
                            className="absolute top-4 right-4 p-2 bg-white/10 rounded-full hover:bg-white/20"
                            onClick={() => setShowFullscreen(false)}
                        >
                            <X className="w-6 h-6 text-white" />
                        </button>
                        <img 
                            src={selectedHistoryItem.result} 
                            alt="Try-on result"
                            className="max-w-full max-h-[90vh] object-contain rounded-2xl"
                        />
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Save Modal */}
            <AnimatePresence>
                {showSaveModal && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-[60] bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4 overflow-y-auto overflow-x-hidden pointer-events-auto"
                        onClick={() => !isSaving && setShowSaveModal(false)}
                        onTouchStart={(e) => e.stopPropagation()}
                    >
                        <motion.div
                            initial={{ y: '100%', opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            exit={{ y: '100%', opacity: 0 }}
                            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                            className="bg-white rounded-t-3xl sm:rounded-2xl shadow-2xl max-w-md w-full p-5 sm:p-6 pb-8 sm:pb-6 max-h-[90vh] overflow-y-auto pointer-events-auto"
                            style={{ paddingBottom: 'calc(1.5rem + env(safe-area-inset-bottom))' }}
                            onClick={e => e.stopPropagation()}
                            onTouchStart={(e) => e.stopPropagation()}
                        >
                            {saveSuccess ? (
                                <div className="text-center py-8">
                                    <div className="w-16 h-16 rounded-full bg-olive-100 flex items-center justify-center mx-auto mb-4">
                                        <CheckCircle2 className="w-8 h-8 text-olive-600" />
                                    </div>
                                    <h3 className="text-lg font-semibold text-stone-900">Outfit Saved!</h3>
                                    <p className="text-sm text-stone-500 mt-2">Your outfit has been saved successfully.</p>
                                </div>
                            ) : (
                                <>
                                    {/* Mobile drag handle */}
                                    <div className="sm:hidden flex justify-center mb-3 -mt-2">
                                        <div className="w-12 h-1 rounded-full bg-stone-300" />
                                    </div>

                                    <div className="flex items-center justify-between mb-5 sm:mb-6">
                                        <h3 className="text-lg sm:text-xl font-semibold text-stone-900">Save Canvas</h3>
                                        <button 
                                            onClick={() => setShowSaveModal(false)} 
                                            className="p-2.5 sm:p-2 hover:bg-stone-100 rounded-full min-w-[44px] min-h-[44px] sm:min-w-0 sm:min-h-0 flex items-center justify-center"
                                        >
                                            <X className="w-5 h-5 text-stone-400" />
                                        </button>
                                    </div>

                                    {/* Outfit Name (optional) */}
                                    <div className="mb-4">
                                        <label className="block text-sm font-medium text-stone-700 mb-1.5">
                                            Outfit Name <span className="text-stone-400 font-normal">(optional)</span>
                                        </label>
                                        <input
                                            type="text"
                                            value={outfitName}
                                            onChange={(e) => setOutfitName(e.target.value)}
                                            placeholder={`Outfit ${new Date().toLocaleDateString()}`}
                                            className="w-full px-4 py-3 sm:py-2.5 rounded-xl border border-stone-200 focus:border-suede-500 focus:ring-2 focus:ring-suede-100 outline-none text-sm sm:text-base"
                                        />
                                    </div>

                                    {/* Category */}
                                    <div className="mb-5">
                                        <label className="block text-sm font-medium text-stone-700 mb-2">Category</label>
                                        <div className="flex flex-wrap gap-2">
                                            {['Casual', 'Work', 'Evening', 'Date Night', 'Vacation', 'Gym', 'Streetwear', 'Formal', 'Weekend', 'Other'].map(cat => (
                                                <button
                                                    key={cat}
                                                    type="button"
                                                    onClick={() => setOutfitCategory(outfitCategory === cat ? '' : cat)}
                                                    className={`px-4 py-2.5 sm:px-3 sm:py-1.5 rounded-full text-sm sm:text-xs font-medium transition-colors min-h-[44px] sm:min-h-0 ${
                                                        outfitCategory === cat
                                                            ? 'bg-suede-500 text-white'
                                                            : 'bg-stone-100 text-stone-600 hover:bg-stone-200 active:bg-stone-300'
                                                    }`}
                                                >
                                                    {cat}
                                                </button>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Board Appearance */}
                                    <div className="mb-5">
                                        <label className="block text-sm font-medium text-stone-700 mb-2">Board Style</label>
                                        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4">
                                            <div className="flex items-center gap-2">
                                                <span className="text-xs text-stone-500 whitespace-nowrap">Background</span>
                                                <div className="flex flex-wrap gap-2 sm:gap-1.5">
                                                    {[
                                                        { color: '#ffffff', label: 'White' },
                                                        { color: '#fafaf9', label: 'Warm White' },
                                                        { color: '#f5f5f4', label: 'Stone' },
                                                        { color: '#e7e5e4', label: 'Grey' },
                                                        { color: '#d5c4a1', label: 'Beige' },
                                                        { color: '#c4a882', label: 'Creamy Brown' },
                                                        { color: '#78604e', label: 'Dark Brown' },
                                                        { color: '#1c1917', label: 'Black' },
                                                    ].map(bg => (
                                                        <button
                                                            key={bg.color}
                                                            type="button"
                                                            onClick={() => setBoardBg(bg.color)}
                                                            title={bg.label}
                                                            className={`w-8 h-8 sm:w-6 sm:h-6 rounded-full border-2 transition-all ${
                                                                boardBg === bg.color
                                                                    ? 'border-suede-500 scale-110 ring-2 ring-suede-200'
                                                                    : 'border-stone-200 hover:border-stone-400'
                                                            }`}
                                                            style={{ backgroundColor: bg.color }}
                                                        />
                                                    ))}
                                                    {/* Custom color picker */}
                                                    <label
                                                        title="Pick custom color"
                                                        className="relative w-8 h-8 sm:w-6 sm:h-6 rounded-full cursor-pointer border-2 border-dashed border-stone-300 hover:border-suede-400 transition-all overflow-hidden flex items-center justify-center"
                                                        style={{ background: `conic-gradient(from 0deg, #e8d5c4, #c4a882, #a67c5b, #78604e, #d5c4a1, #f5e6d3, #e8d5c4)` }}
                                                    >
                                                        <input
                                                            type="color"
                                                            value={boardBg}
                                                            onChange={(e) => setBoardBg(e.target.value)}
                                                            className="absolute inset-0 opacity-0 cursor-pointer w-full h-full"
                                                        />
                                                    </label>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-2 sm:ml-auto">
                                                <span className="text-xs text-stone-500">Grid</span>
                                                <button
                                                    type="button"
                                                    onClick={() => setShowDots(!showDots)}
                                                    className={`relative w-11 h-6 sm:w-9 sm:h-5 rounded-full transition-colors ${
                                                        showDots ? 'bg-suede-500' : 'bg-stone-300'
                                                    }`}
                                                >
                                                    <span className={`absolute top-0.5 w-5 h-5 sm:w-4 sm:h-4 rounded-full bg-white shadow transition-transform ${
                                                        showDots ? 'left-[22px] sm:left-[18px]' : 'left-0.5'
                                                    }`} />
                                                </button>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Preview */}
                                    <div className="mb-5">
                                        <label className="block text-sm font-medium text-stone-700 mb-2">Preview</label>
                                        <div className="rounded-xl overflow-hidden border border-stone-200 relative" style={{ backgroundColor: boardBg, height: 120 }}>
                                            {showDots && (
                                                <div className="absolute inset-0" style={{
                                                    backgroundImage: `radial-gradient(circle, ${['#1c1917','#78604e'].includes(boardBg) ? '#a8a29e' : '#d6d3d1'} 1px, transparent 1px)`,
                                                    backgroundSize: '16px 16px'
                                                }} />
                                            )}
                                            <div className="absolute inset-0 flex items-center justify-center gap-1 p-2">
                                                {canvasItems.slice(0, 5).map(item => (
                                                    <img
                                                        key={item.canvasId}
                                                        src={item.image_url || item.image}
                                                        alt=""
                                                        className="h-16 w-auto object-contain drop-shadow-md"
                                                    />
                                                ))}
                                                {canvasItems.length > 5 && (
                                                    <span className="text-xs font-medium text-stone-400">+{canvasItems.length - 5}</span>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Save Destinations */}
                                    <div className="space-y-3 mb-6">
                                        <label className="block text-sm font-medium text-stone-700 mb-2">Save to:</label>
                                        
                                        {/* Style Studio Saves */}
                                        <label className="flex items-center gap-3 p-3 rounded-xl border border-stone-200 hover:border-suede-300 cursor-pointer transition-colors">
                                            <input
                                                type="checkbox"
                                                checked={saveOptions.toStudioSaves}
                                                onChange={(e) => setSaveOptions(prev => ({ ...prev, toStudioSaves: e.target.checked }))}
                                                className="w-5 h-5 rounded-md border-stone-300 text-suede-600 focus:ring-suede-500"
                                            />
                                            <div className="flex-1">
                                                <div className="flex items-center gap-2">
                                                    <Sparkles className="w-4 h-4 text-suede-500" />
                                                    <span className="font-medium text-stone-800">Style Studio Saves</span>
                                                </div>
                                                <p className="text-xs text-stone-500 mt-0.5">Quick access in Style Studio</p>
                                            </div>
                                        </label>

                                        {/* Stylebook */}
                                        <label className="flex items-start gap-3 p-3 rounded-xl border border-stone-200 hover:border-suede-300 cursor-pointer transition-colors">
                                            <input
                                                type="checkbox"
                                                checked={saveOptions.toStylebook}
                                                onChange={(e) => setSaveOptions(prev => ({ ...prev, toStylebook: e.target.checked }))}
                                                className="w-5 h-5 rounded-md border-stone-300 text-suede-600 focus:ring-suede-500 mt-0.5"
                                            />
                                            <div className="flex-1">
                                                <div className="flex items-center gap-2">
                                                    <Book className="w-4 h-4 text-amber-500" />
                                                    <span className="font-medium text-stone-800">Stylebook</span>
                                                </div>
                                                <p className="text-xs text-stone-500 mt-0.5">Add to your style inspiration</p>
                                                {saveOptions.toStylebook && (
                                                    <>
                                                        <select
                                                            value={saveOptions.stylebookId || ''}
                                                            onChange={(e) => setSaveOptions(prev => ({ ...prev, stylebookId: e.target.value, newStylebookName: '' }))}
                                                            className="mt-2 w-full px-3 py-2 rounded-lg border border-stone-200 text-sm focus:border-suede-500 outline-none"
                                                            onClick={e => e.stopPropagation()}
                                                        >
                                                            <option value="">Select a stylebook...</option>
                                                            <option value="__new__">✨ Create new stylebook</option>
                                                            {stylebooks.map(sb => (
                                                                <option key={sb.id} value={sb.id}>{sb.name}</option>
                                                            ))}
                                                        </select>
                                                        {saveOptions.stylebookId === '__new__' && (
                                                            <input
                                                                type="text"
                                                                value={saveOptions.newStylebookName || ''}
                                                                onChange={(e) => setSaveOptions(prev => ({ ...prev, newStylebookName: e.target.value }))}
                                                                placeholder="New stylebook name..."
                                                                className="mt-2 w-full px-3 py-2 rounded-lg border border-stone-200 text-sm focus:border-suede-500 outline-none"
                                                                onClick={e => e.stopPropagation()}
                                                            />
                                                        )}
                                                    </>
                                                )}
                                            </div>
                                        </label>

                                        {/* Saved Looks */}
                                        <label className="flex items-center gap-3 p-3 rounded-xl border border-stone-200 hover:border-suede-300 cursor-pointer transition-colors">
                                            <input
                                                type="checkbox"
                                                checked={saveOptions.toSavedLooks}
                                                onChange={(e) => setSaveOptions(prev => ({ ...prev, toSavedLooks: e.target.checked }))}
                                                className="w-5 h-5 rounded-md border-stone-300 text-suede-600 focus:ring-suede-500"
                                            />
                                            <div className="flex-1">
                                                <div className="flex items-center gap-2">
                                                    <Heart className="w-4 h-4 text-terracotta-500" />
                                                    <span className="font-medium text-stone-800">Saved Canvases</span>
                                                </div>
                                                <p className="text-xs text-stone-500 mt-0.5">Save to your Saved page for quick reference</p>
                                            </div>
                                        </label>
                                    </div>

                                    {error && (
                                        <div className="mb-4 p-3 bg-red-50 border border-red-100 rounded-xl text-sm text-red-600">
                                            {error}
                                            {typeof error === 'string' && error.includes('try-on limit') && (
                                                <button onClick={() => navigate('/account')} className="mt-2 flex items-center gap-1 px-3 py-1.5 bg-gradient-to-r from-terracotta-500 to-suede-500 text-white text-xs font-semibold rounded-lg active:scale-95 transition-transform">
                                                    <Crown className="w-3 h-3" /> Upgrade Plan
                                                </button>
                                            )}
                                        </div>
                                    )}

                                    <div className="flex gap-2">
                                        <Button
                                            onClick={downloadCanvasImage}
                                            disabled={isSaving}
                                            className="flex-1 bg-stone-100 hover:bg-stone-200 text-stone-800 py-3 rounded-full border border-stone-200"
                                        >
                                            <Download className="w-4 h-4 mr-2" />
                                            Download
                                        </Button>
                                        <Button
                                            onClick={saveOutfit}
                                            disabled={isSaving}
                                            className="flex-1 bg-stone-800 hover:bg-stone-900 text-white py-3 rounded-full"
                                        >
                                            {isSaving ? (
                                                <>
                                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                                    Saving...
                                                </>
                                            ) : (
                                                <>
                                                    <Save className="w-4 h-4 mr-2" />
                                                    {editingOutfitId ? 'Update Canvas' : 'Save Canvas'}
                                                </>
                                            )}
                                        </Button>
                                    </div>
                                </>
                            )}
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Header */}
            <div className="sticky top-[calc(4.25rem+env(safe-area-inset-top,0px))] lg:top-28 z-30 bg-[#faf9f7] border-b border-stone-200">
                <div className="max-w-7xl mx-auto px-4">
                    <div className="flex items-center justify-between h-16">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full border border-stone-300 flex items-center justify-center">
                                <Sparkles className="w-5 h-5 text-stone-600" />
                            </div>
                            <div>
                                <h1 className="text-lg font-light tracking-wide text-stone-800" style={{ fontFamily: 'Georgia, serif' }}>Style Studio</h1>
                                <p className="text-[10px] uppercase tracking-[0.15em] text-stone-400">Build & Try-On Outfits</p>
                            </div>
                        </div>
                        
                        <div className="flex items-center gap-3">
                        </div>
                    </div>

                    {/* Main Tabs */}
                    <div className="flex gap-2 pb-3 pt-1 overflow-x-auto">
                        {/* My Wardrobe tab - visible on mobile, placed first */}
                        <button
                            onClick={() => { setActiveTab('builder'); if (!showWardrobeSidebar) setWardrobePanelMode('half'); setShowWardrobeSidebar(!showWardrobeSidebar); }}
                            className={`lg:hidden px-4 py-2 text-xs uppercase tracking-[0.15em] font-medium rounded-full transition-all whitespace-nowrap flex items-center gap-1.5 ${
                                showWardrobeSidebar
                                    ? 'bg-suede-500 text-white'
                                    : 'text-suede-600 border border-suede-300 bg-suede-50 hover:bg-suede-100'
                            }`}
                        >
                            <Plus className="w-3.5 h-3.5" />
                            Wardrobe
                            {canvasItems.length > 0 && (
                                <span className={`text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center ${
                                    showWardrobeSidebar ? 'bg-white/30 text-white' : 'bg-suede-500 text-white'
                                }`}>{canvasItems.length}</span>
                            )}
                        </button>
                        {[
                            { id: 'builder', label: 'Style Board', icon: Layers },
                            { id: 'tryon', label: 'Try-On', icon: User },
                            { id: 'history', label: 'Saved Canvases', icon: Clock }
                        ].map(tab => (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                className={`px-4 py-2 text-xs uppercase tracking-[0.15em] font-medium rounded-full transition-all whitespace-nowrap ${
                                    activeTab === tab.id
                                        ? 'bg-stone-800 text-white'
                                        : 'text-stone-500 hover:text-stone-700 border border-stone-200 hover:border-stone-300'
                                }`}
                            >
                                <tab.icon className="w-3.5 h-3.5 inline mr-1.5" />
                                {tab.label}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <div className="max-w-7xl mx-auto">
                <AnimatePresence mode="wait">
                    {activeTab === 'builder' && (
                        <motion.div
                            key="builder"
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            className="flex"
                        >
                            {/* Wardrobe Sidebar */}
                            <AnimatePresence>
                                {showWardrobeSidebar && (
                                    <motion.div
                                        initial={{ y: '100%', opacity: 0 }}
                                        animate={{ y: 0, opacity: 1, width: window.innerWidth >= 1024 ? 320 : '100%', height: window.innerWidth < 1024 ? (wardrobePanelMode === 'full' ? '92vh' : '60vh') : 'auto' }}
                                        exit={{ y: '100%', opacity: 0 }}
                                        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                                        className="fixed lg:relative bottom-0 left-0 right-0 lg:inset-auto z-40 lg:z-auto bg-white border-t lg:border-t-0 border-r border-stone-200 rounded-t-2xl lg:rounded-none shadow-[0_-4px_20px_rgba(0,0,0,0.1)] lg:shadow-none lg:h-[calc(100vh-120px)] overflow-hidden flex-shrink-0"
                                        style={{ maxHeight: window.innerWidth < 1024 ? '95vh' : 'none' }}
                                    >
                                        <div className="p-4 h-full flex flex-col">
                                            {/* Mobile drag handle — swipe up for full, down to shrink/close */}
                                            <div 
                                                className="lg:hidden flex flex-col items-center py-2 mb-1 cursor-grab active:cursor-grabbing touch-none select-none -mx-4 px-4 bg-white sticky top-0 z-10"
                                                onTouchStart={(e) => { panelTouchStartY.current = e.touches[0].clientY; }}
                                                onTouchEnd={(e) => {
                                                    const deltaY = e.changedTouches[0].clientY - panelTouchStartY.current;
                                                    if (deltaY > 60) {
                                                        if (wardrobePanelMode === 'full') {
                                                            setWardrobePanelMode('half');
                                                        } else {
                                                            setShowWardrobeSidebar(false);
                                                            setTimeout(() => setWardrobePanelMode('half'), 300);
                                                        }
                                                    } else if (deltaY < -60) {
                                                        setWardrobePanelMode('full');
                                                    }
                                                }}
                                            >
                                                <div className="w-12 h-1.5 rounded-full bg-stone-400" />
                                                <p className="text-[9px] text-stone-400 mt-1.5 tracking-wide uppercase font-medium">Swipe to resize</p>
                                            </div>
                                            <div className="flex items-center justify-between mb-3 -mx-4 px-4 py-2 bg-white sticky top-[42px] lg:top-0 lg:static z-10 border-b lg:border-b-0 border-stone-100">
                                                <h3 className="text-sm font-semibold text-stone-700 flex items-center gap-2">
                                                    <LayoutGrid className="w-4 h-4 text-suede-500" />
                                                    My Wardrobe
                                                    {canvasItems.length > 0 && (
                                                        <span className="bg-suede-500 text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center">
                                                            {canvasItems.length}
                                                        </span>
                                                    )}
                                                </h3>
                                                <button
                                                    onClick={() => { setShowWardrobeSidebar(false); setTimeout(() => setWardrobePanelMode('half'), 300); }}
                                                    className="p-2 hover:bg-stone-100 rounded-lg transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center lg:hidden"
                                                >
                                                    <X className="w-4 h-4" />
                                                </button>
                                            </div>
                                            <p className="text-[11px] text-stone-400 mb-2 -mt-1 px-0">Tap items to add to your style board ✨</p>

                                            {/* Search */}
                                            <div className="relative mb-3">
                                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                                                <input
                                                    type="text"
                                                    value={wardrobeSearch}
                                                    onChange={(e) => setWardrobeSearch(e.target.value)}
                                                    placeholder="Search items..."
                                                    className="w-full pl-9 pr-4 py-2 rounded-full border border-stone-200 text-sm focus:border-stone-400 focus:ring-1 focus:ring-stone-200 outline-none placeholder:text-stone-300"
                                                />
                                            </div>

                                            {/* Category Pills — with scroll fade hint */}
                                            <div className="relative mb-3">
                                                <div className="flex gap-1.5 overflow-x-auto pb-3 pr-6" style={{ WebkitOverflowScrolling: 'touch', scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
                                                    {garmentCategories.map(cat => (
                                                        <button
                                                            key={cat.id}
                                                            onClick={() => setSelectedCategory(cat.id)}
                                                            className={`px-3 py-1.5 rounded-full text-xs uppercase tracking-wider font-medium whitespace-nowrap transition-colors ${
                                                                selectedCategory === cat.id
                                                                    ? 'bg-suede-500 text-white'
                                                                    : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
                                                            }`}
                                                        >
                                                            {cat.label}
                                                        </button>
                                                    ))}
                                                </div>
                                                <div className="absolute right-0 top-0 bottom-3 w-10 bg-gradient-to-l from-white to-transparent pointer-events-none lg:hidden" />
                                            </div>

                                            {/* Items Grid — vertical scroll only */}
                                            <div className="flex-1 overflow-y-auto overflow-x-hidden" style={{ overscrollBehaviorX: 'none' }}>
                                                {selectedCategory === 'ai-studio' ? (
                                                    /* AI Studio Creations grid */
                                                    loadingAiStudio ? (
                                                        <div className="flex items-center justify-center h-40">
                                                            <Loader2 className="w-6 h-6 text-suede-500 animate-spin" />
                                                        </div>
                                                    ) : aiStudioItems.length === 0 ? (
                                                        <div className="text-center py-8 px-4">
                                                            <div className="w-14 h-14 rounded-xl bg-suede-50 flex items-center justify-center mx-auto mb-3">
                                                                <Sparkles className="w-7 h-7 text-suede-300" />
                                                            </div>
                                                            <p className="text-sm font-medium text-stone-700 mb-1">No AI Studio creations yet</p>
                                                            <p className="text-xs text-stone-500 mb-3">Save items from AI Studio to use them here</p>
                                                            <a 
                                                                href="/ai-studio" 
                                                                className="inline-flex items-center gap-1.5 text-xs font-medium text-suede-600 hover:text-suede-700"
                                                            >
                                                                <Sparkles className="w-3.5 h-3.5" />
                                                                Go to AI Studio
                                                            </a>
                                                        </div>
                                                    ) : (
                                                        <div className="grid grid-cols-3 gap-1.5">
                                                            {aiStudioItems.map((item, idx) => (
                                                                <div
                                                                    key={item.product_id || idx}
                                                                    onClick={() => addAiStudioToCanvas(item)}
                                                                    className="cursor-pointer hover:scale-105 transition-transform group relative"
                                                                    title={item.product_name || 'AI Creation'}
                                                                >
                                                                    <img
                                                                        src={item.image_url}
                                                                        alt={item.product_name || `AI Studio ${idx + 1}`}
                                                                        className="w-full aspect-square object-cover rounded-lg"
                                                                    />
                                                                    <div className="absolute inset-0 rounded-lg bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center">
                                                                        <Plus className="w-5 h-5 text-white opacity-0 group-hover:opacity-100 transition-opacity drop-shadow-lg" />
                                                                    </div>
                                                                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent rounded-b-lg px-1.5 py-1">
                                                                        <p className="text-[9px] text-white truncate">{item.product_name || 'AI Creation'}</p>
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )
                                                ) : selectedCategory === 'stickers' ? (
                                                    /* Stickers grid */
                                                    <div>
                                                        {/* Paste from clipboard button */}
                                                        <button
                                                            onClick={pasteFromClipboard}
                                                            disabled={isPasting}
                                                            className="w-full mb-3 p-3 rounded-xl border-2 border-dashed border-amber-300 bg-amber-50/50 hover:border-amber-400 hover:bg-amber-50 transition-all flex items-center justify-center gap-2"
                                                        >
                                                            {isPasting ? (
                                                                <Loader2 className="w-4 h-4 text-amber-600 animate-spin" />
                                                            ) : (
                                                                <Clipboard className="w-4 h-4 text-amber-600" />
                                                            )}
                                                            <span className="text-sm font-medium text-amber-700">
                                                                {isPasting ? 'Pasting...' : 'Paste from Clipboard'}
                                                            </span>
                                                        </button>

                                                        {loadingStickers ? (
                                                            <div className="flex items-center justify-center h-40">
                                                                <Loader2 className="w-6 h-6 text-amber-500 animate-spin" />
                                                            </div>
                                                        ) : stickerItems.length === 0 ? (
                                                            <div className="text-center py-6 px-4">
                                                                <div className="w-14 h-14 rounded-xl bg-amber-50 flex items-center justify-center mx-auto mb-3">
                                                                    <Scissors className="w-7 h-7 text-amber-300" />
                                                                </div>
                                                                <p className="text-sm font-medium text-stone-700 mb-1">No stickers yet</p>
                                                                <p className="text-xs text-stone-500 leading-relaxed">
                                                                    In Photos, long-press a subject → Copy,{'\n'}then come back here and tap Paste
                                                                </p>
                                                                <p className="text-xs text-stone-400 leading-relaxed mt-2">
                                                                    💡 You can also paste stickers saved on your iPhone keyboard!
                                                                </p>
                                                            </div>
                                                        ) : (
                                                            <div className="grid grid-cols-3 gap-1.5">
                                                                {stickerItems.map((item, idx) => (
                                                                    <div
                                                                        key={item.product_id || idx}
                                                                        onClick={() => addStickerToCanvas(item)}
                                                                        className="cursor-pointer hover:scale-105 transition-transform group relative"
                                                                        title={item.product_name || 'Sticker'}
                                                                    >
                                                                        <img
                                                                            src={item.image_url}
                                                                            alt={item.product_name || `Sticker ${idx + 1}`}
                                                                            className="w-full aspect-square object-contain rounded-lg bg-[url('data:image/svg+xml,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2216%22%20height%3D%2216%22%3E%3Crect%20width%3D%228%22%20height%3D%228%22%20fill%3D%22%23f5f5f4%22%2F%3E%3Crect%20x%3D%228%22%20y%3D%228%22%20width%3D%228%22%20height%3D%228%22%20fill%3D%22%23f5f5f4%22%2F%3E%3C%2Fsvg%3E')]"
                                                                        />
                                                                        <div className="absolute inset-0 rounded-lg bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center">
                                                                            <Plus className="w-5 h-5 text-white opacity-0 group-hover:opacity-100 transition-opacity drop-shadow-lg" />
                                                                        </div>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>
                                                ) : (
                                                    /* Wardrobe items grid */
                                                    loadingWardrobe ? (
                                                        <div className="flex items-center justify-center h-40">
                                                            <Loader2 className="w-6 h-6 text-suede-500 animate-spin" />
                                                        </div>
                                                    ) : wardrobeItems.length === 0 ? (
                                                        <div className="text-center py-8 px-4">
                                                            <div className="w-14 h-14 rounded-xl bg-stone-100 flex items-center justify-center mx-auto mb-3">
                                                                <Shirt className="w-7 h-7 text-stone-300" />
                                                            </div>
                                                            <p className="text-sm font-medium text-stone-700 mb-1">No items in wardrobe</p>
                                                            <p className="text-xs text-stone-500 mb-3">Add items to your wardrobe to build outfits</p>
                                                            <a 
                                                                href="/my-wardrobe" 
                                                                className="inline-flex items-center gap-1.5 text-xs font-medium text-suede-600 hover:text-suede-700"
                                                            >
                                                                <Plus className="w-3.5 h-3.5" />
                                                                Go to My Wardrobe
                                                            </a>
                                                        </div>
                                                    ) : filteredWardrobeItems.length === 0 ? (
                                                        <div className="text-center py-8 text-stone-500 text-sm">
                                                            No items match your search
                                                        </div>
                                                    ) : (
                                                        <div className="grid grid-cols-3 gap-1.5">
                                                            {filteredWardrobeItems.map(item => (
                                                                <div
                                                                    key={item.id}
                                                                    draggable
                                                                    onDragStart={(e) => handleDragStart(e, item)}
                                                                    onClick={() => addToCanvas(item)}
                                                                    className="cursor-grab active:cursor-grabbing hover:scale-105 transition-transform group relative"
                                                                    title={item.name}
                                                                >
                                                                    <img
                                                                        src={item.image_url || item.image}
                                                                        alt={item.name}
                                                                        className="w-full aspect-square object-cover rounded-lg"
                                                                    />
                                                                    <div className="absolute inset-0 rounded-lg bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center">
                                                                        <Plus className="w-5 h-5 text-white opacity-0 group-hover:opacity-100 transition-opacity drop-shadow-lg" />
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )
                                                )}
                                            </div>

                                            <div className="pt-3 border-t border-stone-100 mt-3">
                                                <p className="text-xs text-stone-400 text-center">
                                                    {canvasItems.length > 0 
                                                        ? `✅ ${canvasItems.length} ${canvasItems.length === 1 ? 'item' : 'items'} on canvas — tap to add more`
                                                        : 'Tap items or drag to add to your outfit'
                                                    }
                                                </p>
                                            </div>
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>

                            {/* Canvas Area */}
                            <div className="flex-1 pt-5 px-4 pb-4 lg:p-6">
                                <div className="flex flex-col gap-3 mb-4">
                                    {/* Row 1: Title + Edit toggle */}
                                    <div className="flex items-center justify-between gap-3">
                                        <div className="min-w-0">
                                            <h2 className="text-lg font-semibold text-stone-900 leading-normal">{outfitName || 'Outfit Board'}</h2>
                                            <p className="text-sm text-stone-500">
                                                {canvasItems.length} {canvasItems.length === 1 ? 'piece' : 'pieces'}{canvasEditMode ? ' • Editing' : ''}
                                            </p>
                                        </div>
                                        {canvasItems.length > 0 && (
                                            <button
                                                onClick={() => setCanvasEditMode(!canvasEditMode)}
                                                className={`rounded-full px-3.5 py-1.5 flex items-center gap-1.5 transition-all text-xs font-medium uppercase tracking-wider flex-shrink-0 whitespace-nowrap ${
                                                    canvasEditMode 
                                                        ? 'bg-suede-500 text-white shadow-md' 
                                                        : 'bg-white text-stone-600 border border-stone-200 hover:border-stone-300'
                                                }`}
                                            >
                                                {canvasEditMode ? <Move className="w-3.5 h-3.5" /> : <Lock className="w-3.5 h-3.5" />}
                                                {canvasEditMode ? 'Editing' : 'Locked'}
                                            </button>
                                        )}
                                    </div>
                                    
                                    {/* Row 2: Board controls — colors, dots, clear, save */}
                                    <div className="flex items-center justify-between gap-2">
                                        {/* Background swatches */}
                                        <div className="flex items-center gap-1 flex-wrap">
                                            {[
                                                { color: '#ffffff', label: 'White' },
                                                { color: '#f5f5f4', label: 'Stone' },
                                                { color: '#e7e5e4', label: 'Grey' },
                                                { color: '#d5c4a1', label: 'Beige' },
                                                { color: '#c4a882', label: 'Creamy Brown' },
                                                { color: '#78604e', label: 'Dark Brown' },
                                                { color: '#1c1917', label: 'Black' },
                                            ].map(bg => (
                                                <button
                                                    key={bg.color}
                                                    onClick={() => setBoardBg(bg.color)}
                                                    title={bg.label}
                                                    className={`w-5 h-5 rounded-full border-2 transition-all flex-shrink-0 ${
                                                        boardBg === bg.color
                                                            ? 'border-suede-500 scale-110'
                                                            : 'border-stone-300 hover:border-stone-400'
                                                    }`}
                                                    style={{ backgroundColor: bg.color }}
                                                />
                                            ))}
                                            {/* Custom color picker */}
                                            <label
                                                title="Pick custom color"
                                                className="relative w-5 h-5 rounded-full cursor-pointer border-2 border-dashed border-stone-300 hover:border-suede-400 transition-all overflow-hidden flex items-center justify-center flex-shrink-0"
                                                style={{ background: `conic-gradient(from 0deg, #e8d5c4, #c4a882, #a67c5b, #78604e, #d5c4a1, #f5e6d3, #e8d5c4)` }}
                                            >
                                                <input
                                                    type="color"
                                                    value={boardBg}
                                                    onChange={(e) => setBoardBg(e.target.value)}
                                                    className="absolute inset-0 opacity-0 cursor-pointer w-full h-full"
                                                />
                                            </label>
                                            <button
                                                onClick={() => setShowDots(!showDots)}
                                                title={showDots ? 'Hide grid dots' : 'Show grid dots'}
                                                className={`ml-1 px-2 py-1 rounded-md text-[10px] font-medium transition-colors flex-shrink-0 ${
                                                    showDots 
                                                        ? 'bg-stone-200 text-stone-700' 
                                                        : 'bg-stone-100 text-stone-400'
                                                }`}
                                            >
                                                ···
                                            </button>
                                        </div>
                                        
                                        {/* Action buttons */}
                                        {canvasItems.length > 0 && (
                                            <div className="flex items-center gap-2 flex-shrink-0">
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={clearCanvas}
                                                    className="text-red-500 border-red-200 hover:bg-red-50 text-xs px-2.5"
                                                >
                                                    <Trash2 className="w-3.5 h-3.5 mr-1" />
                                                    Clear
                                                </Button>
                                                <Button
                                                    size="sm"
                                                    onClick={() => setShowSaveModal(true)}
                                                    className="bg-stone-800 hover:bg-stone-900 text-white rounded-full text-xs px-3"
                                                >
                                                    <Save className="w-3.5 h-3.5 mr-1" />
                                                    Save
                                                </Button>
                                            </div>
                                        )}
                                    </div>

                                    {/* Locked items bar — unlock controls outside the canvas */}
                                    {canvasEditMode && canvasItems.some(i => i.locked) && (
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <span className="text-[10px] uppercase tracking-wider font-medium text-stone-400">Locked:</span>
                                            {canvasItems.filter(i => i.locked).map(item => (
                                                <button
                                                    key={item.canvasId}
                                                    onClick={() => setCanvasItems(prev => prev.map(ci => ci.canvasId === item.canvasId ? { ...ci, locked: false } : ci))}
                                                    className="flex items-center gap-1 px-2 py-1 bg-stone-800 text-white rounded-full text-[10px] font-medium hover:bg-stone-600 active:bg-stone-500 transition-colors"
                                                >
                                                    <Lock className="w-3 h-3" />
                                                    {item.isText ? (item.textContent || 'Text').substring(0, 12) : item.isShape ? item.shapeType : item.isSticker ? 'Sticker' : (item.name || 'Item').substring(0, 12)}
                                                    <Unlock className="w-3 h-3 ml-0.5 opacity-60" />
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                {error && (
                                    <div className="mb-4 p-3 bg-red-50 border border-red-100 rounded-xl text-sm text-red-600 whitespace-pre-line">
                                        {error}
                                        {typeof error === 'string' && error.includes('try-on limit') && (
                                            <button onClick={() => navigate('/account')} className="mt-2 flex items-center gap-1 px-3 py-1.5 bg-gradient-to-r from-terracotta-500 to-suede-500 text-white text-xs font-semibold rounded-lg active:scale-95 transition-transform">
                                                <Crown className="w-3 h-3" /> Upgrade Plan
                                            </button>
                                        )}
                                    </div>
                                )}

                                {/* Hidden file inputs */}
                                <input ref={photoUploadRef} type="file" accept="image/*" className="hidden" onChange={handlePhotoUploadForCanvas} />
                                <input ref={rawPhotoUploadRef} type="file" accept="image/*" className="hidden" onChange={handleRawPhotoUpload} />
                                <input ref={bgUploadRef} type="file" accept="image/*" className="hidden" onChange={handleBgImageUpload} />

                                {/* Free Tools Bar */}
                                <div className="mb-4">
                                    <button
                                        onClick={() => setShowFreeTools(!showFreeTools)}
                                        className="flex items-center gap-2 text-xs uppercase tracking-[0.12em] font-medium text-stone-500 hover:text-stone-700 transition-colors mb-2"
                                    >
                                        <Sparkles className="w-3.5 h-3.5 text-green-500" />
                                        Free Tools
                                        <ChevronRight className={`w-3.5 h-3.5 transition-transform ${showFreeTools ? 'rotate-90' : ''}`} />
                                    </button>

                                    <AnimatePresence>
                                        {showFreeTools && (
                                            <motion.div
                                                initial={{ height: 0, opacity: 0 }}
                                                animate={{ height: 'auto', opacity: 1 }}
                                                exit={{ height: 0, opacity: 0 }}
                                                className="overflow-hidden"
                                            >
                                                <div className="grid grid-cols-1 gap-3 pb-3">
                                                    {/* Text Section — FREE */}
                                                    <div className="w-full p-4 rounded-xl border-2 border-dashed border-green-200 bg-green-50/30 transition-all">
                                                        <div className="flex items-start gap-3">
                                                            <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 bg-green-100">
                                                                <Type className="w-5 h-5 text-green-600" />
                                                            </div>
                                                            <div className="flex-1">
                                                                <div className="flex items-center gap-2">
                                                                    <p className="text-sm font-medium text-stone-700">Add Text</p>
                                                                    <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded-full bg-green-100 text-green-700 uppercase tracking-wider">Free</span>
                                                                </div>
                                                                <p className="text-[11px] text-stone-400 mt-0.5">
                                                                    Add text boxes to your board
                                                                </p>
                                                            </div>
                                                            <button
                                                                onClick={addTextToCanvas}
                                                                className="px-3 py-1.5 rounded-lg bg-green-600 text-white text-xs font-medium hover:bg-green-700 transition-colors flex items-center gap-1.5"
                                                            >
                                                                <Plus className="w-3 h-3" />
                                                                Add Text
                                                            </button>
                                                        </div>
                                                    </div>

                                                    {/* Upload Photo As-Is — FREE */}
                                                    <div className="w-full p-4 rounded-xl border-2 border-dashed border-green-200 bg-green-50/30 transition-all">
                                                        <div className="flex items-start gap-3">
                                                            <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 bg-green-100">
                                                                {isUploadingPhoto && !removingBg ? (
                                                                    <Loader2 className="w-5 h-5 text-green-600 animate-spin" />
                                                                ) : (
                                                                    <Upload className="w-5 h-5 text-green-600" />
                                                                )}
                                                            </div>
                                                            <div className="flex-1">
                                                                <div className="flex items-center gap-2">
                                                                    <p className="text-sm font-medium text-stone-700">
                                                                        {isUploadingPhoto && !removingBg ? 'Uploading...' : 'Upload Photo'}
                                                                    </p>
                                                                    <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded-full bg-green-100 text-green-700 uppercase tracking-wider">Free</span>
                                                                </div>
                                                                <p className="text-[11px] text-stone-400 mt-0.5">
                                                                    Add photo as-is to canvas
                                                                </p>
                                                            </div>
                                                            <button
                                                                onClick={() => rawPhotoUploadRef.current?.click()}
                                                                disabled={isUploadingPhoto}
                                                                className="px-3 py-1.5 rounded-lg bg-green-600 text-white text-xs font-medium hover:bg-green-700 transition-colors flex items-center gap-1.5"
                                                            >
                                                                <Plus className="w-3 h-3" />
                                                                Upload
                                                            </button>
                                                        </div>
                                                    </div>

                                                    {/* Custom Background Image — FREE */}
                                                    <div className="w-full p-4 rounded-xl border-2 border-dashed border-green-200 bg-green-50/30 transition-all relative">
                                                        <div className="flex items-start gap-3">
                                                            <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 bg-green-100">
                                                                <ImagePlus className="w-5 h-5 text-green-600" />
                                                            </div>
                                                            <div className="flex-1">
                                                                <div className="flex items-center gap-2">
                                                                    <p className="text-sm font-medium text-stone-700">Custom Background</p>
                                                                    <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded-full bg-green-100 text-green-700 uppercase tracking-wider">Free</span>
                                                                </div>
                                                                <p className="text-[11px] text-stone-400 mt-0.5">
                                                                    {boardBgImage ? 'Background set • Click to change' : 'Upload a photo as board background'}
                                                                </p>
                                                            </div>
                                                            <button
                                                                onClick={() => bgUploadRef.current?.click()}
                                                                className="px-3 py-1.5 rounded-lg bg-green-600 text-white text-xs font-medium hover:bg-green-700 transition-colors flex items-center gap-1.5"
                                                            >
                                                                <Plus className="w-3 h-3" />
                                                                Upload
                                                            </button>
                                                        </div>
                                                        {boardBgImage && (
                                                            <button
                                                                onClick={(e) => { e.stopPropagation(); setBoardBgImage(null); }}
                                                                className="absolute top-2 right-2 p-1 bg-white rounded-full shadow-sm border border-stone-200 hover:bg-red-50"
                                                            >
                                                                <X className="w-3 h-3 text-red-500" />
                                                            </button>
                                                        )}
                                                    </div>

                                                    {/* Design Shapes — FREE */}
                                                    <div className="w-full p-4 rounded-xl border-2 border-dashed border-green-200 bg-green-50/30 transition-all">
                                                        <div className="flex items-start gap-3 mb-3">
                                                            <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 bg-green-100">
                                                                <svg className="w-5 h-5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V7a4 4 0 014-4h10a4 4 0 014 4v10a4 4 0 01-4 4H7z" />
                                                                </svg>
                                                            </div>
                                                            <div className="flex-1">
                                                                <div className="flex items-center gap-2">
                                                                    <p className="text-sm font-medium text-stone-700">Design Shapes</p>
                                                                    <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded-full bg-green-100 text-green-700 uppercase tracking-wider">Free</span>
                                                                </div>
                                                                <p className="text-[11px] text-stone-400 mt-0.5">
                                                                    Add shapes to your design board
                                                                </p>
                                                            </div>
                                                            <div className="flex items-center gap-2">
                                                                <label className="text-[11px] text-stone-500 font-medium">Color:</label>
                                                                <input 
                                                                    type="color" 
                                                                    value={shapeColor} 
                                                                    onChange={(e) => setShapeColor(e.target.value)}
                                                                    className="w-8 h-8 rounded border-2 border-stone-300 cursor-pointer"
                                                                />
                                                            </div>
                                                        </div>
                                                        <div className="grid grid-cols-4 gap-2">
                                                            <button
                                                                onClick={() => addShapeToCanvas('line')}
                                                                className="px-3 py-2.5 rounded-lg border-2 border-stone-200 hover:border-green-400 bg-white transition-all hover:scale-105 flex items-center justify-center gap-2"
                                                            >
                                                                <div className="w-5 h-0.5 rounded-full" style={{ backgroundColor: shapeColor }} />
                                                                <span className="text-xs font-medium text-stone-600">Line</span>
                                                            </button>
                                                            <button
                                                                onClick={() => addShapeToCanvas('circle')}
                                                                className="px-3 py-2.5 rounded-lg border-2 border-stone-200 hover:border-green-400 bg-white transition-all hover:scale-105 flex items-center justify-center gap-2"
                                                            >
                                                                <div className="w-4 h-4 rounded-full border-2" style={{ borderColor: shapeColor }} />
                                                                <span className="text-xs font-medium text-stone-600">Circle</span>
                                                            </button>
                                                            <button
                                                                onClick={() => addShapeToCanvas('rectangle')}
                                                                className="px-3 py-2.5 rounded-lg border-2 border-stone-200 hover:border-green-400 bg-white transition-all hover:scale-105 flex items-center justify-center gap-2"
                                                            >
                                                                <div className="w-5 h-3 border-2 rounded-sm" style={{ borderColor: shapeColor }} />
                                                                <span className="text-xs font-medium text-stone-600">Rect</span>
                                                            </button>
                                                            <button
                                                                onClick={() => addShapeToCanvas('oval')}
                                                                className="px-3 py-2.5 rounded-lg border-2 border-stone-200 hover:border-green-400 bg-white transition-all hover:scale-105 flex items-center justify-center gap-2"
                                                            >
                                                                <div className="w-5 h-3 rounded-full border-2" style={{ borderColor: shapeColor }} />
                                                                <span className="text-xs font-medium text-stone-600">Oval</span>
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </div>

                                {/* Subscription Tools Bar */}
                                <div className="mb-4">
                                    <button
                                        onClick={() => setShowPremiumTools(!showPremiumTools)}
                                        className="flex items-center gap-2 text-xs uppercase tracking-[0.12em] font-medium text-stone-500 hover:text-stone-700 transition-colors mb-2"
                                    >
                                        <Crown className="w-3.5 h-3.5 text-amber-500" />
                                        Subscription Tools
                                        <ChevronRight className={`w-3.5 h-3.5 transition-transform ${showPremiumTools ? 'rotate-90' : ''}`} />
                                    </button>

                                    <AnimatePresence>
                                        {showPremiumTools && (
                                            <motion.div
                                                initial={{ height: 0, opacity: 0 }}
                                                animate={{ height: 'auto', opacity: 1 }}
                                                exit={{ height: 0, opacity: 0 }}
                                                className="overflow-hidden"
                                            >
                                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pb-3">
                                                    {/* Upload Photo with BG Removal */}
                                                    <div className="relative">
                                                        <button
                                                            onClick={() => isPremiumUser ? photoUploadRef.current?.click() : null}
                                                            disabled={removingBg}
                                                            className={`w-full p-4 rounded-xl border-2 border-dashed transition-all text-left ${
                                                                isPremiumUser
                                                                    ? 'border-suede-300 bg-suede-50/50 hover:border-suede-400 hover:bg-suede-50 cursor-pointer'
                                                                    : 'border-stone-200 bg-stone-50 cursor-not-allowed opacity-70'
                                                            }`}
                                                        >
                                                            <div className="flex items-start gap-3">
                                                                <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                                                                    isPremiumUser ? 'bg-suede-100' : 'bg-stone-100'
                                                                }`}>
                                                                    {removingBg ? (
                                                                        <Loader2 className="w-5 h-5 text-suede-500 animate-spin" />
                                                                    ) : (
                                                                        <Upload className="w-5 h-5 text-suede-500" />
                                                                    )}
                                                                </div>
                                                                <div className="min-w-0">
                                                                    <p className="text-sm font-medium text-stone-700">
                                                                        {removingBg ? 'Removing BG...' : 'Upload & Remove BG'}
                                                                    </p>
                                                                    <p className="text-[11px] text-stone-400 mt-0.5">
                                                                        Auto-removes background & adds to canvas
                                                                    </p>
                                                                </div>
                                                            </div>
                                                        </button>
                                                        {!isPremiumUser && (
                                                            <div className="absolute inset-0 rounded-xl bg-stone-900/5 backdrop-blur-[1px] flex items-center justify-center">
                                                                <div className="flex items-center gap-1.5 bg-stone-800 text-white px-3 py-1.5 rounded-full text-xs font-medium shadow-lg">
                                                                    <Lock className="w-3 h-3" />
                                                                    Basic / Premium
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>

                                                    {/* Virtual Try-On Models */}
                                                    <div className="relative">
                                                        <div className={`w-full p-4 rounded-xl border-2 border-dashed transition-all ${
                                                            isPremiumUser
                                                                ? 'border-suede-300 bg-suede-50/50'
                                                                : 'border-stone-200 bg-stone-50 opacity-70'
                                                        }`}>
                                                            <div className="flex items-start gap-3 mb-2">
                                                                <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                                                                    isPremiumUser ? 'bg-suede-100' : 'bg-stone-100'
                                                                }`}>
                                                                    <UserCircle2 className="w-5 h-5 text-suede-500" />
                                                                </div>
                                                                <div className="min-w-0">
                                                                    <p className="text-sm font-medium text-stone-700">Try-On Models</p>
                                                                    <p className="text-[11px] text-stone-400 mt-0.5">
                                                                        Add saved try-on looks to canvas
                                                                    </p>
                                                                </div>
                                                            </div>
                                                            {isPremiumUser && (
                                                                <div className="mt-2">
                                                                    {loadingTryOnModels ? (
                                                                        <div className="flex items-center justify-center py-3">
                                                                            <Loader2 className="w-4 h-4 text-suede-500 animate-spin" />
                                                                        </div>
                                                                    ) : tryOnModels.length === 0 ? (
                                                                        <p className="text-[11px] text-stone-400 text-center py-2">
                                                                            No try-on results yet. Use Virtual Try-On first!
                                                                        </p>
                                                                    ) : (
                                                                        <div className="flex gap-1.5 overflow-x-auto pb-1">
                                                                            {tryOnModels.map((model, idx) => (
                                                                                <button
                                                                                    key={model.id || idx}
                                                                                    onClick={() => addTryOnModelToCanvas(model)}
                                                                                    className="flex-shrink-0 w-14 h-14 rounded-lg overflow-hidden border-2 border-stone-200 hover:border-suede-400 transition-all hover:scale-105"
                                                                                    title="Click to add to canvas"
                                                                                >
                                                                                    <img
                                                                                        src={model.result_image_url}
                                                                                        alt={`Try-on ${idx + 1}`}
                                                                                        className="w-full h-full object-cover"
                                                                                    />
                                                                                </button>
                                                                            ))}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            )}
                                                        </div>
                                                        {!isPremiumUser && (
                                                            <div className="absolute inset-0 rounded-xl bg-stone-900/5 backdrop-blur-[1px] flex items-center justify-center">
                                                                <div className="flex items-center gap-1.5 bg-stone-800 text-white px-3 py-1.5 rounded-full text-xs font-medium shadow-lg">
                                                                    <Lock className="w-3 h-3" />
                                                                    Basic / Premium
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>

                                                </div>
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </div>

                                {/* Floating Edit Toolbar — appears above canvas when an item is selected */}
                                {canvasEditMode && selectedCanvasItem && (() => {
                                    const selItem = canvasItems.find(i => i.canvasId === selectedCanvasItem);
                                    if (!selItem || selItem.locked) return null;
                                    return (
                                        <div className="flex items-center gap-2 flex-wrap bg-white rounded-xl shadow-sm border border-stone-200 px-3 py-2 mb-3">
                                            {/* Item type label */}
                                            <span className="text-[10px] uppercase tracking-wider font-medium text-stone-400 mr-1">
                                                {selItem.isShape ? 'Shape' : selItem.isText ? 'Text' : selItem.isSticker ? 'Sticker' : selItem.isAiStudio ? 'AI Studio' : 'Wardrobe'}
                                            </span>

                                            {/* Size control — for images (wardrobe, stickers, AI studio) */}
                                            {!selItem.isShape && !selItem.isText && (
                                                <div className="flex items-center gap-1.5">
                                                    <span className="text-[9px] text-stone-400">Size</span>
                                                    <input
                                                        type="range" min="60" max="400"
                                                        value={selItem.w || 140}
                                                        onChange={(e) => {
                                                            const val = parseInt(e.target.value);
                                                            if (!isNaN(val)) setCanvasItems(prev => prev.map(ci => ci.canvasId === selItem.canvasId ? { ...ci, w: val } : ci));
                                                        }}
                                                        className="w-20 h-1.5 accent-suede-500 cursor-pointer"
                                                        style={{ touchAction: 'manipulation' }}
                                                    />
                                                    <span className="text-[9px] text-stone-500">{selItem.w || 140}px</span>
                                                    <div className="w-px h-5 bg-stone-200" />
                                                </div>
                                            )}

                                            {/* Shape controls: width + height + thickness + color */}
                                            {selItem.isShape && (
                                                <>
                                                    <div className="flex items-center gap-1.5">
                                                        <span className="text-[9px] text-stone-400">W</span>
                                                        <input
                                                            type="range" min="30" max="800"
                                                            value={selItem.w || 140}
                                                            onChange={(e) => {
                                                                const val = parseInt(e.target.value);
                                                                if (!isNaN(val)) {
                                                                    const oldW = selItem.w || 140;
                                                                    const canvasW = canvasRef.current?.getBoundingClientRect().width || 1;
                                                                    const dxPct = ((val - oldW) / 2) / canvasW * 100;
                                                                    setCanvasItems(prev => prev.map(ci => ci.canvasId === selItem.canvasId ? { ...ci, w: val, x: ci.x - dxPct } : ci));
                                                                }
                                                            }}
                                                            className="w-14 h-1.5 accent-suede-500 cursor-pointer"
                                                            style={{ touchAction: 'manipulation' }}
                                                        />
                                                        <span className="text-[9px] text-stone-500">{selItem.w || 140}</span>
                                                    </div>
                                                    <div className="flex items-center gap-1.5">
                                                        <span className="text-[9px] text-stone-400">H</span>
                                                        <input
                                                            type="range" min="20" max="1000"
                                                            value={selItem.h || selItem.w || 140}
                                                            onChange={(e) => {
                                                                const val = parseInt(e.target.value);
                                                                if (!isNaN(val)) {
                                                                    const oldH = selItem.h || selItem.w || 140;
                                                                    const canvasH = canvasRef.current?.getBoundingClientRect().height || 1;
                                                                    const dyPct = ((val - oldH) / 2) / canvasH * 100;
                                                                    setCanvasItems(prev => prev.map(ci => ci.canvasId === selItem.canvasId ? { ...ci, h: val, y: ci.y - dyPct } : ci));
                                                                }
                                                            }}
                                                            className="w-14 h-1.5 accent-violet-500 cursor-pointer"
                                                            style={{ touchAction: 'manipulation' }}
                                                        />
                                                        <span className="text-[9px] text-stone-500">{selItem.h || selItem.w || 140}</span>
                                                    </div>
                                                    <div className="flex items-center gap-1.5">
                                                        <span className="text-[9px] text-stone-400">Thick</span>
                                                        <input
                                                            type="range" min="0.5" max="40" step="0.5"
                                                            value={selItem.strokeWidth || 6}
                                                            onChange={(e) => {
                                                                const val = parseFloat(e.target.value);
                                                                if (!isNaN(val)) setCanvasItems(prev => prev.map(ci => ci.canvasId === selItem.canvasId ? { ...ci, strokeWidth: val } : ci));
                                                            }}
                                                            className="w-14 h-1.5 accent-suede-500 cursor-pointer"
                                                            style={{ touchAction: 'manipulation' }}
                                                        />
                                                    </div>
                                                    <input
                                                        type="color"
                                                        value={selItem.color || '#000000'}
                                                        onChange={(e) => setCanvasItems(prev => prev.map(ci => ci.canvasId === selItem.canvasId ? { ...ci, color: e.target.value } : ci))}
                                                        className="w-6 h-6 rounded border border-stone-300 cursor-pointer"
                                                    />
                                                    <div className="w-px h-5 bg-stone-200" />
                                                </>
                                            )}

                                            {/* Text controls: content, size, color, bold, italic, font */}
                                            {selItem.isText && (
                                                <>
                                                    <textarea
                                                        value={selItem.textContent || ''}
                                                        onChange={(e) => setCanvasItems(prev => prev.map(ci => ci.canvasId === selItem.canvasId ? { ...ci, textContent: e.target.value } : ci))}
                                                        className="w-28 text-xs border border-stone-200 rounded-md px-1.5 py-1 resize-none focus:outline-none focus:ring-1 focus:ring-suede-400"
                                                        rows={1}
                                                        placeholder="Type here..."
                                                    />
                                                    <div className="flex items-center gap-1.5">
                                                        <span className="text-[9px] text-stone-400">Size</span>
                                                        <input
                                                            type="range" min="10" max="48"
                                                            value={selItem.fontSize || 18}
                                                            onChange={(e) => {
                                                                const val = parseInt(e.target.value);
                                                                if (!isNaN(val)) setCanvasItems(prev => prev.map(ci => ci.canvasId === selItem.canvasId ? { ...ci, fontSize: val } : ci));
                                                            }}
                                                            className="w-14 h-1.5 accent-suede-500 cursor-pointer"
                                                            style={{ touchAction: 'manipulation' }}
                                                        />
                                                        <span className="text-[9px] text-stone-500">{selItem.fontSize || 18}px</span>
                                                    </div>
                                                    <input
                                                        type="color"
                                                        value={selItem.fontColor || '#000000'}
                                                        onChange={(e) => setCanvasItems(prev => prev.map(ci => ci.canvasId === selItem.canvasId ? { ...ci, fontColor: e.target.value } : ci))}
                                                        className="w-6 h-6 rounded border border-stone-300 cursor-pointer"
                                                    />
                                                    <button
                                                        onClick={() => setCanvasItems(prev => prev.map(ci => ci.canvasId === selItem.canvasId ? { ...ci, fontWeight: ci.fontWeight === 'bold' ? 'normal' : 'bold' } : ci))}
                                                        className={`p-1 rounded transition-colors ${selItem.fontWeight === 'bold' ? 'bg-suede-500 text-white' : 'bg-stone-100 text-stone-500 hover:bg-stone-200'}`}
                                                    >
                                                        <Bold className="w-3 h-3" />
                                                    </button>
                                                    <button
                                                        onClick={() => setCanvasItems(prev => prev.map(ci => ci.canvasId === selItem.canvasId ? { ...ci, fontStyle: ci.fontStyle === 'italic' ? 'normal' : 'italic' } : ci))}
                                                        className={`p-1 rounded transition-colors ${selItem.fontStyle === 'italic' ? 'bg-suede-500 text-white' : 'bg-stone-100 text-stone-500 hover:bg-stone-200'}`}
                                                    >
                                                        <Italic className="w-3 h-3" />
                                                    </button>
                                                    <select
                                                        value={selItem.fontFamily || 'Inter'}
                                                        onChange={(e) => setCanvasItems(prev => prev.map(ci => ci.canvasId === selItem.canvasId ? { ...ci, fontFamily: e.target.value } : ci))}
                                                        className="text-[10px] border border-stone-200 rounded-md px-1 py-1 focus:outline-none focus:ring-1 focus:ring-suede-400 bg-white"
                                                    >
                                                        <option value="Inter">Inter</option>
                                                        <option value="Georgia">Georgia</option>
                                                        <option value="Times New Roman">Times</option>
                                                        <option value="Courier New">Courier</option>
                                                        <option value="Arial">Arial</option>
                                                        <option value="Verdana">Verdana</option>
                                                        <option value="Trebuchet MS">Trebuchet</option>
                                                        <option value="Palatino">Palatino</option>
                                                        <option value="Garamond">Garamond</option>
                                                        <option value="cursive">Script</option>
                                                        <option value="fantasy">Decorative</option>
                                                    </select>
                                                    <div className="w-px h-5 bg-stone-200" />
                                                </>
                                            )}

                                            {/* Layer controls — all item types */}
                                            {canvasItems.length > 1 && (
                                                <>
                                                    <button
                                                        onClick={() => sendToBack(selItem.canvasId)}
                                                        className="p-1.5 hover:bg-stone-100 rounded transition-colors" title="Send Back"
                                                    >
                                                        <ArrowDown className="w-3.5 h-3.5 text-stone-500" />
                                                    </button>
                                                    <button
                                                        onClick={() => bringToFront(selItem.canvasId)}
                                                        className="p-1.5 hover:bg-stone-100 rounded transition-colors" title="Bring Front"
                                                    >
                                                        <ArrowUp className="w-3.5 h-3.5 text-stone-500" />
                                                    </button>
                                                </>
                                            )}

                                            <div className="w-px h-5 bg-stone-200" />

                                            {/* Lock */}
                                            <button
                                                onClick={() => { setCanvasItems(prev => prev.map(ci => ci.canvasId === selItem.canvasId ? { ...ci, locked: true } : ci)); setSelectedCanvasItem(null); }}
                                                className="p-1.5 hover:bg-stone-100 rounded transition-colors" title="Lock item"
                                            >
                                                <Lock className="w-3.5 h-3.5 text-stone-500" />
                                            </button>

                                            {/* Delete */}
                                            <button
                                                onClick={() => removeFromCanvas(selItem.canvasId)}
                                                className="p-1.5 hover:bg-red-50 rounded transition-colors" title="Remove"
                                            >
                                                <X className="w-3.5 h-3.5 text-red-500" />
                                            </button>

                                            {/* Deselect */}
                                            <button
                                                onClick={() => setSelectedCanvasItem(null)}
                                                className="ml-auto text-[10px] text-stone-400 hover:text-stone-600 uppercase tracking-wider font-medium"
                                            >
                                                Done
                                            </button>
                                        </div>
                                    );
                                })()}

                                {/* Whiteboard Canvas */}
                                <div
                                    ref={canvasRef}
                                    onDrop={handleCanvasDrop}
                                    onDragOver={handleCanvasDragOver}
                                    onDragLeave={handleCanvasDragLeave}
                                    onClick={() => { setSelectedCanvasItem(null); setEditingTextItem(null); }}
                                    className={`relative rounded-2xl border-2 transition-colors overflow-visible ${
                                        dragActive 
                                            ? 'border-suede-400' 
                                            : 'border-stone-200'
                                    }`}
                                    style={{ 
                                        height: '70vh', minHeight: 500, 
                                        isolation: 'isolate', zIndex: 0,
                                        touchAction: 'pan-y',
                                        userSelect: 'none',
                                        WebkitUserSelect: 'none',
                                        backgroundColor: dragActive ? undefined : boardBg,
                                        backgroundImage: boardBgImage
                                            ? `url(${boardBgImage})`
                                            : showDots 
                                                ? `radial-gradient(circle, ${['#1c1917','#78604e'].includes(boardBg) ? '#a8a29e' : '#e7e5e4'} 1px, transparent 1px)` 
                                                : 'none', 
                                        backgroundSize: boardBgImage ? 'contain' : '24px 24px',
                                        backgroundPosition: boardBgImage ? 'center' : undefined,
                                        backgroundRepeat: boardBgImage ? 'no-repeat' : undefined,
                                        cursor: activeCanvasDrag ? 'grabbing' : 'default'
                                    }}
                                >
                                    {canvasItems.length === 0 && (
                                        <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-8 pointer-events-none">
                                            <div className="w-16 h-16 rounded-2xl bg-stone-100/80 flex items-center justify-center mb-4">
                                                <Layers className="w-8 h-8 text-stone-300" />
                                            </div>
                                            <h3 className="text-lg font-medium text-stone-500 mb-1">
                                                Drop pieces here
                                            </h3>
                                            <p className="text-sm text-stone-400 max-w-xs">
                                                Drag from your wardrobe and place anywhere. Overlap, stack, and arrange freely.
                                            </p>
                                            {!showWardrobeSidebar && (
                                                <Button
                                                    variant="outline"
                                                    className="mt-4 pointer-events-auto"
                                                    onClick={() => setShowWardrobeSidebar(true)}
                                                >
                                                    <Plus className="w-4 h-4 mr-2" />
                                                    Add Clothes
                                                </Button>
                                            )}
                                        </div>
                                    )}

                                    {/* Free-positioned garment images */}
                                    {canvasItems.map((item) => (
                                        <div
                                            key={item.canvasId}
                                            onTouchStart={(e) => {
                                                // The <p>'s own onTouchStart stopPropagation prevents
                                                // this from firing when user touches text during editing
                                                if (!item.locked && canvasEditMode && e.touches.length === 1) {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    if (editingTextItem) setEditingTextItem(null);
                                                    // Initiate drag directly from touch — don't rely on pointerdown
                                                    if (canvasRef.current) {
                                                        const touch = e.touches[0];
                                                        const rect = canvasRef.current.getBoundingClientRect();
                                                        const ci = canvasItemsRef.current.find(i => i.canvasId === item.canvasId);
                                                        if (ci) {
                                                            const itemLeftPx = (ci.x / 100) * rect.width;
                                                            const itemTopPx = (ci.y / 100) * rect.height;
                                                            setActiveCanvasDrag({
                                                                canvasId: item.canvasId,
                                                                offsetX: touch.clientX - rect.left - itemLeftPx,
                                                                offsetY: touch.clientY - rect.top - itemTopPx
                                                            });
                                                            bringToFront(item.canvasId);
                                                            setSelectedCanvasItem(item.canvasId);
                                                        }
                                                    }
                                                }
                                            }}
                                            onPointerDown={(e) => {
                                                // Mouse/pen only — touch is handled by onTouchStart above
                                                if (item.locked || e.pointerType === 'touch') return;
                                                handleCanvasItemPointerDown(e, item.canvasId);
                                            }}
                                            onClick={(e) => {
                                                if (item.locked) return;
                                                e.stopPropagation();
                                                if (!canvasEditMode) return;
                                                // If already selected and it's a text item, enter inline edit mode
                                                if (selectedCanvasItem === item.canvasId && item.isText && !editingTextItem) {
                                                    setEditingTextItem(item.canvasId);
                                                    return;
                                                }
                                                if (selectedCanvasItem === item.canvasId) {
                                                    setSelectedCanvasItem(null);
                                                    setEditingTextItem(null);
                                                } else {
                                                    setSelectedCanvasItem(item.canvasId);
                                                    setEditingTextItem(null);
                                                    bringToFront(item.canvasId);
                                                }
                                            }}
                                            className={`absolute group ${
                                                canvasEditMode && activeCanvasDrag?.canvasId === item.canvasId ? 'cursor-grabbing' : canvasEditMode && !item.locked ? 'cursor-grab' : 'cursor-default'
                                            }`}
                                            data-canvas-item
                                            style={{
                                                left: `${item.x}%`,
                                                top: `${item.y}%`,
                                                width: item.w || 140,
                                                zIndex: item.zIndex || 1,
                                                transform: `rotate(${item.rotation || 0}deg)`,
                                                pointerEvents: !canvasEditMode || item.locked ? 'none' : (item.isShape ? 'none' : 'auto'),
                                                touchAction: canvasEditMode && !item.locked ? 'none' : 'auto',
                                                transition: activeCanvasDrag?.canvasId === item.canvasId || activeRotation?.canvasId === item.canvasId || activeResize?.canvasId === item.canvasId || activeHeightResize?.canvasId === item.canvasId ? 'none' : 'box-shadow 0.15s, transform 0.15s',
                                            }}
                                        >
                                            {/* Render shape if it's a shape item, text if text, otherwise render image */}
                                            {item.isShape ? (
                                                <div 
                                                    className={`w-full ${item.locked ? 'opacity-90' : ''}`}
                                                    style={{ height: item.h || (item.w || 140), filter: 'drop-shadow(0 4px 3px rgb(0 0 0 / 0.07))', pointerEvents: canvasEditMode && !item.locked ? 'auto' : 'none', touchAction: canvasEditMode && !item.locked ? 'none' : 'auto' }}
                                                >
                                                    {item.shapeType === 'line' && (
                                                        <svg viewBox="0 0 100 100" className="w-full h-full">
                                                            <line x1="10" y1="50" x2="90" y2="50" stroke={item.color} strokeWidth={item.strokeWidth || 6} strokeLinecap="round" />
                                                        </svg>
                                                    )}
                                                    {item.shapeType === 'circle' && (
                                                        <svg viewBox="0 0 100 100" className="w-full h-full">
                                                            {(item.strokeWidth || 6) >= 40 
                                                                ? <circle cx="50" cy="50" r="45" fill={item.color} />
                                                                : <circle cx="50" cy="50" r="45" fill="none" stroke={item.color} strokeWidth={item.strokeWidth || 6} />
                                                            }
                                                        </svg>
                                                    )}
                                                    {item.shapeType === 'rectangle' && (
                                                        <svg viewBox="0 0 100 100" className="w-full h-full">
                                                            {(item.strokeWidth || 6) >= 40
                                                                ? <rect x="10" y="25" width="80" height="50" fill={item.color} />
                                                                : <rect x="10" y="25" width="80" height="50" fill="none" stroke={item.color} strokeWidth={item.strokeWidth || 6} />
                                                            }
                                                        </svg>
                                                    )}
                                                    {item.shapeType === 'oval' && (
                                                        <svg viewBox="0 0 100 100" className="w-full h-full">
                                                            {(item.strokeWidth || 6) >= 40
                                                                ? <ellipse cx="50" cy="50" rx="45" ry="30" fill={item.color} />
                                                                : <ellipse cx="50" cy="50" rx="45" ry="30" fill="none" stroke={item.color} strokeWidth={item.strokeWidth || 6} />
                                                            }
                                                        </svg>
                                                    )}
                                                </div>
                                            ) : item.isText ? (
                                                <div 
                                                    className={`w-full min-h-[32px] px-1 ${
                                                        selectedCanvasItem === item.canvasId && !item.locked ? 'ring-2 ring-inset ring-suede-400 rounded-lg' : ''
                                                    } ${item.locked ? 'opacity-90' : ''}`}
                                                    style={{ filter: 'drop-shadow(0 2px 2px rgb(0 0 0 / 0.05))' }}
                                                >
                                                    <p
                                                        contentEditable={editingTextItem === item.canvasId}
                                                        suppressContentEditableWarning
                                                        onFocus={(e) => {
                                                            // Place cursor at end when entering edit mode
                                                            if (editingTextItem === item.canvasId) {
                                                                const range = document.createRange();
                                                                range.selectNodeContents(e.target);
                                                                range.collapse(false);
                                                                const sel = window.getSelection();
                                                                sel.removeAllRanges();
                                                                sel.addRange(range);
                                                            }
                                                        }}
                                                        onBlur={(e) => {
                                                            if (editingTextItem === item.canvasId) {
                                                                const newText = e.target.innerText;
                                                                setCanvasItems(prev => prev.map(ci => ci.canvasId === item.canvasId ? { ...ci, textContent: newText } : ci));
                                                                setEditingTextItem(null);
                                                            }
                                                        }}
                                                        onKeyDown={(e) => {
                                                            // Stop propagation so typing doesn't trigger shortcuts
                                                            e.stopPropagation();
                                                            if (e.key === 'Enter' && !e.shiftKey) {
                                                                e.preventDefault();
                                                                e.target.blur();
                                                            }
                                                        }}
                                                        onPointerDown={(e) => {
                                                            // When editing, don't start drag
                                                            if (editingTextItem === item.canvasId) e.stopPropagation();
                                                        }}
                                                        onTouchStart={(e) => {
                                                            // When editing, allow normal touch for text cursor
                                                            if (editingTextItem === item.canvasId) e.stopPropagation();
                                                        }}
                                                        ref={editingTextItem === item.canvasId ? (el) => { if (el && document.activeElement !== el) el.focus(); } : undefined}
                                                        style={{
                                                            fontFamily: item.fontFamily || 'Inter',
                                                            fontSize: `${item.fontSize || 18}px`,
                                                            color: item.fontColor || '#000000',
                                                            fontWeight: item.fontWeight || 'normal',
                                                            fontStyle: item.fontStyle || 'normal',
                                                            lineHeight: 1.3,
                                                            margin: 0,
                                                            whiteSpace: 'pre-wrap',
                                                            wordBreak: 'break-word',
                                                            userSelect: editingTextItem === item.canvasId ? 'text' : 'none',
                                                            WebkitUserSelect: editingTextItem === item.canvasId ? 'text' : 'none',
                                                            outline: 'none',
                                                            cursor: editingTextItem === item.canvasId ? 'text' : 'inherit',
                                                            minWidth: 40,
                                                        }}
                                                    >
                                                        {item.textContent || 'Text'}
                                                    </p>
                                                </div>
                                            ) : (
                                                <img
                                                    src={item.image_url || item.image}
                                                    alt={item.name || 'garment'}
                                                    className={`w-full h-auto object-contain drop-shadow-lg pointer-events-none select-none ${
                                                        selectedCanvasItem === item.canvasId ? 'ring-2 ring-inset ring-suede-400 rounded-lg' : ''
                                                    }`}
                                                    draggable={false}
                                                />
                                            )}
                                            
                                            {/* Hover/selected controls — only in edit mode, not locked */}
                                            {canvasEditMode && !item.locked && (
                                            <div className={`absolute -top-3 -right-3 flex gap-1 transition-opacity pointer-events-auto ${
                                                selectedCanvasItem === item.canvasId ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
                                            }`}>
                                                {/* Lock button — all item types */}
                                                <button
                                                    onPointerDown={(e) => e.stopPropagation()}
                                                    onClick={(e) => { e.stopPropagation(); setCanvasItems(prev => prev.map(ci => ci.canvasId === item.canvasId ? { ...ci, locked: true } : ci)); setSelectedCanvasItem(null); }}
                                                    className="p-1 bg-white rounded-full shadow-md hover:bg-stone-50 border border-stone-200"
                                                    title="Lock item"
                                                >
                                                    <Unlock className="w-3 h-3 text-stone-500" />
                                                </button>
                                                <button
                                                    onPointerDown={(e) => e.stopPropagation()}
                                                    onClick={(e) => { e.stopPropagation(); removeFromCanvas(item.canvasId); }}
                                                    className="p-1 bg-white rounded-full shadow-md hover:bg-red-50 border border-stone-200"
                                                >
                                                    <X className="w-3 h-3 text-red-500" />
                                                </button>
                                            </div>
                                            )}







                                            {/* Rotation handle — visible when selected in edit mode */}
                                            {canvasEditMode && selectedCanvasItem === item.canvasId && !item.locked && (
                                                <div className="absolute -top-10 left-1/2 -translate-x-1/2 flex flex-col items-center z-10 pointer-events-auto">
                                                    <div
                                                        onPointerDown={(e) => handleRotatePointerDown(e, item.canvasId)}
                                                        onTouchStart={(e) => e.stopPropagation()}
                                                        className="w-5 h-5 rounded-full bg-suede-500 border-2 border-white shadow-lg cursor-grab active:cursor-grabbing touch-none flex items-center justify-center"
                                                        title="Drag to rotate"
                                                    >
                                                        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                                            <path d="M1 4v6h6" /><path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" />
                                                        </svg>
                                                    </div>
                                                    <div className="w-px h-3 bg-suede-400" />
                                                </div>
                                            )}

                                            {/* Bottom-right resize handle — text items only, when selected */}
                                            {canvasEditMode && selectedCanvasItem === item.canvasId && !item.locked && item.isText && (
                                                <div
                                                    onPointerDown={(e) => handleResizePointerDown(e, item.canvasId)}
                                                    onTouchStart={(e) => { e.stopPropagation(); }}
                                                    className="absolute -bottom-2.5 -right-2.5 w-5 h-5 rounded-full bg-suede-500 border-2 border-white shadow-lg cursor-se-resize touch-none z-20 pointer-events-auto flex items-center justify-center"
                                                    title="Drag to resize"
                                                >
                                                    <svg width="8" height="8" viewBox="0 0 8 8" fill="none" stroke="white" strokeWidth="1.5" strokeLinecap="round">
                                                        <path d="M1 7L7 1" /><path d="M4 7L7 4" />
                                                    </svg>
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>

                                {/* Floating add button — outside the canvas board */}
                                {canvasItems.length > 0 && !showWardrobeSidebar && (
                                    <div className="flex justify-end mt-3 lg:hidden">
                                        <button
                                            onClick={() => { setWardrobePanelMode('half'); setShowWardrobeSidebar(true); }}
                                            className="bg-suede-500 hover:bg-suede-600 text-white rounded-full px-4 py-2.5 flex items-center gap-2 shadow-lg shadow-suede-500/30 transition-all hover:scale-105"
                                            title="Add clothes"
                                        >
                                            <Plus className="w-4 h-4" />
                                            <span className="text-xs font-medium uppercase tracking-wider">Add Clothes</span>
                                        </button>
                                    </div>
                                )}
                            </div>
                        </motion.div>
                    )}

                    {activeTab === 'tryon' && (
                        <motion.div
                            key="tryon"
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            className="p-4 lg:p-6"
                        >
                            <div className="max-w-6xl mx-auto">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                                    {/* Left: Photo Upload & Result */}
                                    <div className="bg-white rounded-2xl shadow-sm border border-stone-200 p-4 md:p-6">
                                        <h3 className="text-xs uppercase tracking-[0.15em] font-medium text-stone-500 mb-4 flex items-center gap-2">
                                            <User className="w-4 h-4 text-stone-400" />
                                            Your Photo
                                        </h3>

                                        {!userPhotoPreview ? (
                                            <label className="block cursor-pointer">
                                                <div className="aspect-[3/4] max-h-[60vh] md:max-h-none rounded-xl border-2 border-dashed border-stone-200 flex flex-col items-center justify-center hover:border-suede-300 hover:bg-suede-50/50 transition-colors">
                                                    <Upload className="w-8 h-8 md:w-10 md:h-10 text-stone-300 mb-2 md:mb-3" />
                                                    <p className="text-xs md:text-sm font-medium text-stone-600">Upload your photo</p>
                                                    <p className="text-[10px] md:text-xs text-stone-400 mt-1">Full-body, front-facing</p>
                                                </div>
                                                <input
                                                    type="file"
                                                    accept="image/*"
                                                    onChange={(e) => e.target.files?.[0] && handleFile(e.target.files[0])}
                                                    className="hidden"
                                                />
                                            </label>
                                        ) : (
                                            <div className="relative">
                                                <div className="aspect-[3/4] max-h-[60vh] md:max-h-none rounded-xl overflow-hidden bg-stone-100">
                                                    <img
                                                        src={tryOnResult || userPhotoPreview}
                                                        alt="Your photo"
                                                        className="w-full h-full object-cover"
                                                    />
                                                </div>
                                                {tryOnResult && (
                                                    <div className="absolute bottom-3 left-3 right-3 flex gap-2">
                                                        <button
                                                            onClick={() => downloadResult()}
                                                            className="flex-1 py-2 bg-white/90 backdrop-blur-sm rounded-full shadow-lg hover:bg-white flex items-center justify-center gap-1.5 text-xs font-medium text-stone-700"
                                                        >
                                                            <Download className="w-3.5 h-3.5" />
                                                            Save
                                                        </button>
                                                    </div>
                                                )}
                                                <button
                                                    onClick={() => {
                                                        setUserPhoto(null);
                                                        setUserPhotoPreview(null);
                                                        setTryOnResult(null);
                                                    }}
                                                    className="absolute top-3 right-3 p-2 bg-white rounded-full shadow-lg hover:bg-stone-50"
                                                >
                                                    <X className="w-4 h-4 text-stone-600" />
                                                </button>
                                            </div>
                                        )}

                                        {isProcessing && (
                                            <div className="mt-4 p-4 bg-suede-50 rounded-xl">
                                                <div className="flex items-center gap-3">
                                                    <Loader2 className="w-5 h-5 text-suede-500 animate-spin" />
                                                    <span className="text-sm text-suede-700">{processingSteps[processingStep].text}</span>
                                                </div>
                                            </div>
                                        )}

                                        {/* Inline try-on result history */}
                                        {tryOnHistory.length > 0 && (
                                            <div className="mt-4">
                                                <p className="text-xs font-medium text-stone-500 mb-2">Recent Results</p>
                                                <div className="flex gap-2 overflow-x-auto pb-1">
                                                    {tryOnHistory.slice(0, 6).map((item, idx) => (
                                                        <button
                                                            key={item.id}
                                                            onClick={() => {
                                                                setTryOnResult(item.result);
                                                                setSelectedHistoryItem(item);
                                                            }}
                                                            className={`flex-shrink-0 w-14 h-14 rounded-lg overflow-hidden border-2 transition-all hover:scale-105 ${
                                                                tryOnResult === item.result ? 'border-suede-500' : 'border-stone-200'
                                                            }`}
                                                        >
                                                            <img src={item.result} alt={`Result ${idx + 1}`} className="w-full h-full object-cover" />
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    {/* Right: Garment Selection — multi-select up to 4 */}
                                    <div className="bg-white rounded-2xl shadow-sm border border-stone-200 p-6">
                                        <h3 className="text-xs uppercase tracking-[0.15em] font-medium text-stone-500 mb-2 flex items-center gap-2">
                                            <Shirt className="w-4 h-4 text-stone-400" />
                                            Select Garments
                                            <span className="text-[10px] font-normal text-stone-400">(up to 4)</span>
                                        </h3>

                                        {/* Selected garments preview */}
                                        {selectedTryOnGarments.length > 0 && (
                                            <div className="mb-3 p-2.5 bg-suede-50 rounded-xl">
                                                <div className="flex items-center gap-2">
                                                    {selectedTryOnGarments.map((g, idx) => (
                                                        <div key={idx} className="relative">
                                                            <img
                                                                src={g.image_url || g.image}
                                                                alt={g.name || ''}
                                                                className="w-11 h-11 rounded-lg object-cover border-2 border-suede-400"
                                                            />
                                                            <button
                                                                onClick={() => setSelectedTryOnGarments(prev => prev.filter((_, i) => i !== idx))}
                                                                className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-red-500 rounded-full flex items-center justify-center shadow-sm"
                                                            >
                                                                <X className="w-2.5 h-2.5 text-white" />
                                                            </button>
                                                        </div>
                                                    ))}
                                                    <p className="text-xs text-suede-600 ml-1">{selectedTryOnGarments.length}/4 selected</p>
                                                </div>
                                            </div>
                                        )}

                                        {/* Category filter */}
                                        <div className="relative mb-3">
                                            <div className="flex gap-1.5 overflow-x-auto pb-2 pr-6" style={{ WebkitOverflowScrolling: 'touch', scrollbarWidth: 'none' }}>
                                                {['all', 'Tops', 'Bottoms', 'Dresses', 'Outerwear', 'Accessories'].map(cat => (
                                                    <button
                                                        key={cat}
                                                        onClick={() => setTryOnCategoryFilter(cat)}
                                                        className={`px-2.5 py-1 rounded-full text-[10px] uppercase tracking-wider font-medium whitespace-nowrap transition-colors ${
                                                            tryOnCategoryFilter === cat
                                                                ? 'bg-stone-800 text-white'
                                                                : 'bg-stone-100 text-stone-500 hover:bg-stone-200'
                                                        }`}
                                                    >
                                                        {cat === 'all' ? 'All' : cat}
                                                    </button>
                                                ))}
                                            </div>
                                            <div className="absolute right-0 top-0 bottom-2 w-8 bg-gradient-to-l from-white to-transparent pointer-events-none" />
                                        </div>

                                        {/* Wardrobe items grid — clean organized layout */}
                                        <div className="grid grid-cols-3 gap-2.5 max-h-[360px] overflow-y-auto overflow-x-hidden pr-1" style={{ scrollbarWidth: 'none' }}>
                                            {wardrobeItems
                                                .filter(item => {
                                                    if (tryOnCategoryFilter === 'all') return true;
                                                    const cat = item.primary_category || item.display_category || item.category || '';
                                                    return cat.toLowerCase() === tryOnCategoryFilter.toLowerCase();
                                                })
                                                .map(item => {
                                                    const isSelected = selectedTryOnGarments.some(g => (g.id || g.canvasId) === (item.id || item.canvasId));
                                                    return (
                                                        <button
                                                            key={item.id}
                                                            onClick={() => {
                                                                if (isSelected) {
                                                                    setSelectedTryOnGarments(prev => prev.filter(g => (g.id || g.canvasId) !== (item.id || item.canvasId)));
                                                                } else if (selectedTryOnGarments.length < 4) {
                                                                    setSelectedTryOnGarments(prev => [...prev, item]);
                                                                    setSelectedGarmentForTryon(item);
                                                                }
                                                            }}
                                                            className={`relative rounded-xl overflow-hidden border-2 transition-all ${
                                                                isSelected
                                                                    ? 'border-suede-500 ring-2 ring-suede-200'
                                                                    : selectedTryOnGarments.length >= 4
                                                                        ? 'border-stone-100 opacity-40 cursor-not-allowed'
                                                                        : 'border-stone-100 hover:border-stone-300'
                                                            }`}
                                                        >
                                                            <div className="aspect-square bg-stone-50 flex items-center justify-center p-1.5">
                                                                <img
                                                                    src={item.image_url || item.image}
                                                                    alt={item.name}
                                                                    className="max-w-full max-h-full object-contain"
                                                                />
                                                            </div>
                                                            <div className="px-1.5 py-1 bg-white">
                                                                <p className="text-[9px] text-stone-500 truncate text-center">{item.name || item.primary_category || 'Item'}</p>
                                                            </div>
                                                            {isSelected && (
                                                                <div className="absolute top-1.5 right-1.5 w-5 h-5 bg-suede-500 rounded-full flex items-center justify-center shadow-sm">
                                                                    <Check className="w-3 h-3 text-white" />
                                                                </div>
                                                            )}
                                                        </button>
                                                    );
                                                })}
                                        </div>

                                        {wardrobeItems.length === 0 && (
                                            <div className="text-center py-6 px-4 bg-stone-50 rounded-xl">
                                                <Shirt className="w-8 h-8 text-stone-300 mx-auto mb-2" />
                                                <p className="text-xs text-stone-500 mb-2">No items in your wardrobe</p>
                                                <a 
                                                    href="/my-wardrobe" 
                                                    className="inline-flex items-center gap-1 text-xs font-medium text-suede-600 hover:text-suede-700"
                                                >
                                                    <Plus className="w-3 h-3" />
                                                    Add items
                                                </a>
                                            </div>
                                        )}

                                        {error && (
                                            <div className="mt-4 p-3 bg-red-50 border border-red-100 rounded-xl text-sm text-red-600 whitespace-pre-line">
                                                {error}
                                                {typeof error === 'string' && error.includes('try-on limit') && (
                                                    <button onClick={() => navigate('/account')} className="mt-2 flex items-center gap-1 px-3 py-1.5 bg-gradient-to-r from-terracotta-500 to-suede-500 text-white text-xs font-semibold rounded-lg active:scale-95 transition-transform">
                                                        <Crown className="w-3 h-3" /> Upgrade Plan
                                                    </button>
                                                )}
                                            </div>
                                        )}

                                        <Button
                                            onClick={() => {
                                                // Use first selected garment for try-on (API is single-garment)
                                                if (selectedTryOnGarments.length > 0) {
                                                    setSelectedGarmentForTryon(selectedTryOnGarments[0]);
                                                }
                                                generateTryOn();
                                            }}
                                            disabled={!userPhoto || selectedTryOnGarments.length === 0 || isProcessing}
                                            className="w-full mt-4 bg-stone-800 hover:bg-stone-900 text-white rounded-full"
                                        >
                                            {isProcessing ? (
                                                <>
                                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                                    Processing...
                                                </>
                                            ) : (
                                                <>
                                                    <Wand2 className="w-4 h-4 mr-2" />
                                                    Generate Try-On ({selectedTryOnGarments.length} {selectedTryOnGarments.length === 1 ? 'item' : 'items'})
                                                </>
                                            )}
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    )}

                    {activeTab === 'history' && (
                        <motion.div
                            key="history"
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            className="p-4 lg:p-6"
                        >
                            {/* Saved Outfits */}
                            <div className="mb-8">
                                <h3 className="text-xs uppercase tracking-[0.15em] font-medium text-stone-500 mb-4 flex items-center gap-2">
                                    <Layers className="w-5 h-5 text-stone-400" />
                                    Saved Outfits
                                </h3>
                                
                                {savedOutfits.length === 0 ? (
                                    <div className="bg-white rounded-2xl shadow-sm border border-stone-200 p-8 text-center">
                                        <div className="w-16 h-16 rounded-2xl bg-stone-100 flex items-center justify-center mx-auto mb-4">
                                            <Layers className="w-8 h-8 text-stone-300" />
                                        </div>
                                        <h4 className="font-medium text-stone-700 mb-2">No saved outfits yet</h4>
                                        <p className="text-sm text-stone-500 mb-4">
                                            Build an outfit and save it to see it here
                                        </p>
                                        <Button
                                            variant="outline"
                                            onClick={() => setActiveTab('builder')}
                                        >
                                            <Plus className="w-4 h-4 mr-2" />
                                            Create Outfit
                                        </Button>
                                    </div>
                                ) : (
                                    <>
                                        {/* Category filter pills */}
                                        {(() => {
                                            const cats = [...new Set(savedOutfits.map(o => o.category || 'Uncategorized'))];
                                            return cats.length > 1 ? (
                                                <div className="flex gap-2 mb-4 overflow-x-auto pb-1">
                                                    <button
                                                        onClick={() => setSelectedSavedCategory('all')}
                                                        className={`px-3 py-1.5 rounded-full text-xs uppercase tracking-wider font-medium whitespace-nowrap transition-colors ${
                                                            selectedSavedCategory === 'all' ? 'bg-stone-800 text-white' : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
                                                        }`}
                                                    >
                                                        All ({savedOutfits.length})
                                                    </button>
                                                    {cats.map(cat => (
                                                        <button
                                                            key={cat}
                                                            onClick={() => setSelectedSavedCategory(cat)}
                                                            className={`px-3 py-1.5 rounded-full text-xs uppercase tracking-wider font-medium whitespace-nowrap transition-colors ${
                                                                selectedSavedCategory === cat ? 'bg-stone-800 text-white' : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
                                                            }`}
                                                        >
                                                            {cat} ({savedOutfits.filter(o => (o.category || 'Uncategorized') === cat).length})
                                                        </button>
                                                    ))}
                                                </div>
                                            ) : null;
                                        })()}

                                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                                            {savedOutfits
                                                .filter(o => selectedSavedCategory === 'all' || (o.category || 'Uncategorized') === selectedSavedCategory)
                                                .map((outfit, index) => (
                                                <motion.div
                                                    key={outfit.id}
                                                    initial={{ opacity: 0, y: 20 }}
                                                    animate={{ opacity: 1, y: 0 }}
                                                    transition={{ delay: index * 0.05 }}
                                                    className="bg-white rounded-xl shadow-sm border border-stone-200 overflow-hidden group cursor-pointer hover:shadow-lg transition-shadow"
                                                    onClick={() => {
                                                        // Resolve full wardrobe item data by ID (saved outfits have stripped images)
                                                        const resolvedItems = outfit.items.map(i => {
                                                            // Shapes: restore directly — no wardrobe lookup needed
                                                            if (i.isShape) {
                                                                return {
                                                                    ...i,
                                                                    canvasId: `shape-${Date.now()}-${Math.random()}`,
                                                                    x: i.x ?? 20 + Math.random() * 40,
                                                                    y: i.y ?? 10 + Math.random() * 40,
                                                                    w: i.w ?? 100,
                                                                    zIndex: i.zIndex ?? 1
                                                                };
                                                            }
                                                            // Stickers: restore directly — they have Cloudinary URLs
                                                            if (i.isSticker) {
                                                                return {
                                                                    ...i,
                                                                    canvasId: `sticker-${Date.now()}-${Math.random()}`,
                                                                    x: i.x ?? 20 + Math.random() * 40,
                                                                    y: i.y ?? 10 + Math.random() * 40,
                                                                    w: i.w ?? 140,
                                                                    zIndex: i.zIndex ?? 1
                                                                };
                                                            }
                                                            // Text: restore directly
                                                            if (i.isText) {
                                                                return {
                                                                    ...i,
                                                                    canvasId: `text-${Date.now()}-${Math.random()}`,
                                                                    x: i.x ?? 20 + Math.random() * 40,
                                                                    y: i.y ?? 10 + Math.random() * 40,
                                                                    w: i.w ?? 140,
                                                                    zIndex: i.zIndex ?? 1
                                                                };
                                                            }
                                                            const fullItem = wardrobeItems.find(w => w.id === i.id);
                                                            return { 
                                                                ...(fullItem || i), 
                                                                canvasId: `${i.id}-${Date.now()}-${Math.random()}`, 
                                                                x: i.x ?? 20 + Math.random() * 40, 
                                                                y: i.y ?? 10 + Math.random() * 40, 
                                                                w: i.w ?? 140, 
                                                                zIndex: i.zIndex ?? 1 
                                                            };
                                                        });
                                                        setCanvasItems(resolvedItems);
                                                        setCanvasEditMode(false); // Open in view-only — user taps Edit to unlock
                                                        setSelectedCanvasItem(null);
                                                        setEditingOutfitId(outfit.id); // Track for update-in-place
                                                        setOutfitName(outfit.name);
                                                        setOutfitCategory(outfit.category || '');
                                                        setBoardBg(outfit.boardBg || '#ffffff');
                                                        setBoardBgImage(outfit.boardBgImage || null);
                                                        setShowDots(outfit.showDots ?? true);
                                                        setActiveTab('builder');
                                                    }}
                                                >
                                                    <div className="aspect-square relative overflow-hidden" style={{ backgroundColor: outfit.boardBg || '#f5f5f4' }}>
                                                        {/* Delete button */}
                                                        <button
                                                            onClick={(e) => deleteOutfit(outfit.id, e)}
                                                            className="absolute top-2 right-2 p-1.5 bg-white/90 backdrop-blur-sm rounded-full shadow-sm z-10 hover:bg-red-50 transition-colors"
                                                            title="Delete outfit"
                                                        >
                                                            <Trash2 className="w-3.5 h-3.5 text-red-400" />
                                                        </button>
                                                        {(outfit.showDots !== false) && (
                                                            <div className="absolute inset-0" style={{
                                                                backgroundImage: `radial-gradient(circle, ${['#1c1917','#78604e'].includes(outfit.boardBg || '#ffffff') ? '#a8a29e' : '#d6d3d1'} 0.8px, transparent 0.8px)`,
                                                                backgroundSize: '12px 12px',
                                                                opacity: 0.5
                                                            }} />
                                                        )}
                                                        <div className="absolute inset-0 flex items-center justify-center p-2">
                                                            {outfit.thumbnail && outfit.thumbnail.length > 50 ? (
                                                                <img
                                                                    src={outfit.thumbnail}
                                                                    alt={outfit.name}
                                                                    className="w-full h-full object-cover"
                                                                />
                                                            ) : outfit.items.slice(0, 5).map((item, i) => {
                                                                // Render shape thumbnail
                                                                if (item.isShape) {
                                                                    return (
                                                                        <div key={i} className="h-[40%] aspect-square" style={{ zIndex: item.zIndex || i }}>
                                                                            <svg viewBox="0 0 100 100" className="w-full h-full">
                                                                                {item.shapeType === 'line' && <line x1="10" y1="50" x2="90" y2="50" stroke={item.color || '#000'} strokeWidth={item.strokeWidth || 6} strokeLinecap="round" />}
                                                                                {item.shapeType === 'circle' && <circle cx="50" cy="50" r="45" fill={item.strokeWidth >= 40 ? item.color : 'none'} stroke={item.color || '#000'} strokeWidth={item.strokeWidth || 6} />}
                                                                                {item.shapeType === 'rectangle' && <rect x="10" y="25" width="80" height="50" fill={item.strokeWidth >= 40 ? item.color : 'none'} stroke={item.color || '#000'} strokeWidth={item.strokeWidth || 6} />}
                                                                                {item.shapeType === 'oval' && <ellipse cx="50" cy="50" rx="45" ry="30" fill={item.strokeWidth >= 40 ? item.color : 'none'} stroke={item.color || '#000'} strokeWidth={item.strokeWidth || 6} />}
                                                                            </svg>
                                                                        </div>
                                                                    );
                                                                }
                                                                // Render text item
                                                                if (item.isText) {
                                                                    return (
                                                                        <div key={i} className="px-1 max-w-full" style={{ zIndex: item.zIndex || i }}>
                                                                            <p className="text-[8px] leading-tight truncate" style={{
                                                                                color: item.fontColor || '#000',
                                                                                fontFamily: item.fontFamily || 'Inter',
                                                                                fontWeight: item.fontWeight || 'normal',
                                                                                fontStyle: item.fontStyle || 'normal',
                                                                            }}>{item.textContent || 'Text'}</p>
                                                                        </div>
                                                                    );
                                                                }
                                                                // Render sticker
                                                                if (item.isSticker && item.image_url && item.image_url.length > 50) {
                                                                    return (
                                                                        <img
                                                                            key={i}
                                                                            src={item.image_url}
                                                                            alt=""
                                                                            className="h-[60%] w-auto object-contain drop-shadow-md -mx-1"
                                                                            style={{ zIndex: item.zIndex || i }}
                                                                        />
                                                                    );
                                                                }
                                                                // Resolve full image from wardrobe
                                                                const fullItem = wardrobeItems.find(w => w.id === item.id);
                                                                const imgSrc = fullItem?.image_url || fullItem?.image || item.image_url || item.image;
                                                                return imgSrc && imgSrc.length > 50 ? (
                                                                    <img
                                                                        key={i}
                                                                        src={imgSrc}
                                                                        alt=""
                                                                        className="h-[70%] w-auto object-contain drop-shadow-md -mx-1"
                                                                        style={{ zIndex: item.zIndex || i }}
                                                                    />
                                                                ) : (
                                                                    <div key={i} className="h-[60%] aspect-square rounded-lg bg-stone-200 flex items-center justify-center">
                                                                        <Shirt className="w-6 h-6 text-stone-400" />
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                        {outfit.items.length > 5 && (
                                                            <div className="absolute bottom-2 right-2 px-1.5 py-0.5 bg-black/60 rounded-full">
                                                                <span className="text-[9px] text-white">+{outfit.items.length - 5}</span>
                                                            </div>
                                                        )}
                                                        {outfit.category && outfit.category !== 'Uncategorized' && (
                                                            <div className="absolute top-2 left-2 px-1.5 py-0.5 bg-white/90 backdrop-blur-sm rounded-full shadow-sm">
                                                                <span className="text-[9px] font-medium text-stone-700">{outfit.category}</span>
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div className="p-3">
                                                        <p className="text-sm font-medium text-stone-800 truncate">{outfit.name}</p>
                                                        <p className="text-xs text-stone-400">
                                                            {outfit.items.length} items • {new Date(outfit.createdAt).toLocaleDateString()}
                                                        </p>
                                                    </div>
                                                </motion.div>
                                            ))}
                                        </div>
                                    </>
                                )}
                            </div>

                            {/* Try-On History */}
                            <div>
                                <h3 className="text-xs uppercase tracking-[0.15em] font-medium text-stone-500 mb-4 flex items-center gap-2">
                                    <Clock className="w-5 h-5 text-stone-400" />
                                    Try-On History
                                </h3>

                                {tryOnHistory.length === 0 ? (
                                    <div className="bg-white rounded-2xl shadow-sm border border-stone-200 p-8 text-center">
                                        <div className="w-16 h-16 rounded-2xl bg-stone-100 flex items-center justify-center mx-auto mb-4">
                                            <Clock className="w-8 h-8 text-stone-300" />
                                        </div>
                                        <h4 className="font-medium text-stone-700 mb-2">No try-ons yet</h4>
                                        <p className="text-sm text-stone-500 mb-4">
                                            Your virtual try-on results will appear here
                                        </p>
                                        <Button
                                            variant="outline"
                                            onClick={() => setActiveTab('tryon')}
                                        >
                                            <User className="w-4 h-4 mr-2" />
                                            Try Something On
                                        </Button>
                                    </div>
                                ) : (
                                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                                        {tryOnHistory.map((item, index) => (
                                            <motion.div
                                                key={item.id}
                                                initial={{ opacity: 0, y: 20 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                transition={{ delay: index * 0.05 }}
                                                className="bg-white rounded-2xl shadow-sm border border-stone-200 overflow-hidden group cursor-pointer hover:shadow-lg transition-shadow"
                                                onClick={() => {
                                                    setSelectedHistoryItem(item);
                                                    setShowFullscreen(true);
                                                }}
                                            >
                                                <div className="aspect-[3/4] relative bg-stone-100">
                                                    <img 
                                                        src={item.result} 
                                                        alt="Try-on result"
                                                        className="w-full h-full object-cover"
                                                    />
                                                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                                                        <div className="absolute bottom-3 left-3 right-3">
                                                            <p className="text-white text-xs font-medium truncate">{item.garment.name}</p>
                                                            <p className="text-white/70 text-[10px]">
                                                                {new Date(item.timestamp).toLocaleDateString()}
                                                            </p>
                                                        </div>
                                                    </div>
                                                    <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity">
                                                        <div className="p-1.5 bg-white/20 backdrop-blur-sm rounded-full">
                                                            <Maximize2 className="w-3.5 h-3.5 text-white" />
                                                        </div>
                                                    </div>
                                                </div>
                                            </motion.div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </div>
    );
}
